import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Upload, Link2, X, FileText, Download, Folder, Tag, Search, Eye, EyeOff, MailQuestion, Bell, Lock, AlertCircle, AlertTriangle, TrendingUp, User as UserIcon, BookUser, Sparkles, Loader2, RefreshCw, RotateCw, ExternalLink, Mail, Copy, CalendarClock, Archive, ArchiveRestore } from "lucide-react";
import { api, API } from "../lib/api";
import { useFileUpload, UploadProgressBar, UploadDetailedProgress } from "../lib/useFileUpload";
import AgentDirectoryModal from "../components/AgentDirectoryModal";
import AssetShareLinkModal from "../components/AssetShareLinkModal";
import { AssetCoverThumbnail } from "../components/AssetCoverThumbnail";
import AdminMagicRequests from "../components/AdminMagicRequests";
import ListinoDriveVerify from "../components/admin/ListinoDriveVerify";
import { MagicSearch } from "./Suppliers";

const emptySupplier = { name: "", description: "", lead_time_days: 0, discount_pct: 0, discount_chain: [], notes: "", website: "", purchase_discount_pct: 0, purchase_discount_chain: [], payment_terms: "", agent_name: "", agent_phone: "", agent_email: "", company_contact_name: "", contact_phone: "", orders_email: "", year_end_bonus_budget: 0, price_increase_pct: 0, price_increase_date: "", price_increase_new_listino: false, order_deadline_date: "" };

const ASSET_KINDS = [
  { value: "catalogo", label: "Catalogo" },
  { value: "listino", label: "Listino" },
  { value: "finiture", label: "Finiture" },
  { value: "cad_3d", label: "File CAD/3D" },
  { value: "comunicazioni", label: "Comunicazioni", adminOnly: true },
  { value: "altro", label: "Altro" },
];

const KIND_LABEL = ASSET_KINDS.reduce((acc, k) => { acc[k.value] = k.label; return acc; }, {});

/**
 * Status panel per Magic Search: mostra quanti PDF sono stati indicizzati,
 * lo stato corrente (in coda, errori, saltati) e permette di avviare manualmente
 * il backfill o di metterlo in pausa.
 */
function MagicSearchStatusPanel() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/assets/indexing-status");
      setStatus(r.data);
    } catch (e) {
      console.error("indexing-status fail", e);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000); // refresh ogni 15s
    return () => clearInterval(t);
  }, [load]);

  if (!status) return null;
  const pct = status.total_pdf > 0
    ? Math.round((status.indexed / status.total_pdf) * 100)
    : 0;
  const inProgress = status.indexing > 0 || status.pending > 0;

  const action = async (path, label) => {
    setBusy(true);
    try {
      const r = await api.post(path);
      toast.success(`${label}: ${JSON.stringify(r.data)}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="bg-cream border border-gold/40 rounded-sm p-4 shadow-[0_2px_10px_-4px_rgba(176,138,82,0.2)]"
      data-testid="magic-search-status-panel"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Search size={14} className="text-gold" />
          <div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-gold font-medium">
              Magic Search · Indicizzazione PDF
            </div>
            <div className="text-sm text-ink/85 mt-0.5">
              <b className="text-ink">{status.indexed}</b>
              <span className="text-slate1"> / {status.total_pdf}</span> PDF indicizzati
              {status.indexing > 0 && <span className="ml-2 text-amber-700">· {status.indexing} in corso</span>}
              {status.pending > 0 && <span className="ml-2 text-slate1">· {status.pending} in coda</span>}
              {status.paused > 0 && <span className="ml-2 text-rose-700">· {status.paused} in pausa</span>}
              {status.skipped > 0 && <span className="ml-2 text-amber-700">· {status.skipped} saltati (file &gt; {status.max_file_mb} MB)</span>}
              {status.errors > 0 && <span className="ml-2 text-rose-700">· {status.errors} errori</span>}
              <span className="ml-2 text-slate1">· {status.indexed_pages_total.toLocaleString("it-IT")} pagine</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {status.pending > 0 && (
            <button
              onClick={() => action("/admin/assets/start-backfill", "Avviato")}
              disabled={busy}
              className="px-3 py-1.5 bg-gold text-cream text-[10px] tracking-[0.2em] uppercase rounded-sm hover:bg-gold/90 disabled:opacity-50"
              data-testid="magic-start-backfill"
            >
              Avvia ora
            </button>
          )}
          {inProgress && (
            <button
              onClick={() => action("/admin/assets/pause-indexing", "In pausa")}
              disabled={busy}
              className="px-3 py-1.5 border border-ink/20 text-ink/80 text-[10px] tracking-[0.2em] uppercase rounded-sm hover:bg-ink/5 disabled:opacity-50"
              data-testid="magic-pause-indexing"
            >
              Metti in pausa
            </button>
          )}
          {status.paused > 0 && (
            <button
              onClick={() => action("/admin/assets/resume-indexing", "Ripreso")}
              disabled={busy}
              className="px-3 py-1.5 bg-gold/10 border border-gold text-gold text-[10px] tracking-[0.2em] uppercase rounded-sm hover:bg-gold/20 disabled:opacity-50"
              data-testid="magic-resume-indexing"
            >
              Riprendi
            </button>
          )}
        </div>
      </div>
      {status.total_pdf > 0 && (
        <div className="mt-3 h-1.5 bg-ink/10 rounded-sm overflow-hidden">
          <div
            className="h-full bg-gold transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
      <div className="mt-2 text-[10px] tracking-[0.15em] uppercase text-slate1">
        Cron notturno automatico alle 02:00 · {pct}% completato
      </div>
    </div>
  );
}

// Auto-detect asset kind from a file name. Falls back to current default.
function detectKindFromFilename(name, currentKind) {
  const n = (name || "").toLowerCase();
  if (/(^|[\s_-])(listin|price[\s_-]?list|prezzi)/i.test(n)) return "listino";
  if (/(catalog|catalogue|brochure|book|lookbook)/i.test(n)) return "catalogo";
  if (/(finiture|finishes|materic|samples?|swatch)/i.test(n)) return "finiture";
  if (/(\.dwg|\.dxf|\.3dm|\.skp|\.fbx|\.obj|\.stp|\.step|cad|3d)/i.test(n)) return "cad_3d";
  return currentKind || "catalogo";
}

// Compute cascaded effective discount from a chain like [50, 10, 10] → 59.5
function cascadePct(chain) {
  if (!Array.isArray(chain) || chain.length === 0) return 0;
  const valid = chain.map((v) => Number(v) || 0).filter((v) => v > 0);
  if (valid.length === 0) return 0;
  let f = 1;
  for (const v of valid) f *= 1 - v / 100;
  return Math.round((1 - f) * 10000) / 100;
}

function formatChain(chain) {
  if (!Array.isArray(chain) || chain.length === 0) return "";
  return chain.filter((v) => Number(v) > 0).map((v) => Number(v)).join(" + ");
}

/**
 * Calcola lo stato dell'aumento listino del fornitore.
 * Restituisce { state, days, pct, date } oppure null se nessun aumento configurato.
 * Stati:
 *  - "imminent": entro 30gg nel futuro → badge ambra pulsante
 *  - "today":   entra in vigore oggi → badge rosso
 *  - "active":  entrato in vigore negli ultimi 7gg → badge rosso
 *  - "future":  oltre 30gg nel futuro → badge ambra normale
 *  - "past":    in vigore da oltre 7gg → badge grigio attenuato
 *  - "set":     aumento configurato ma senza data → badge ambra normale
 */
function getIncreaseAlert(supplier) {
  const pct = Number(supplier?.price_increase_pct || 0);
  const newListino = Boolean(supplier?.price_increase_new_listino);
  if (pct <= 0 && !newListino) return null;
  const dateStr = supplier?.price_increase_date || "";
  if (!dateStr) return { state: "set", days: null, pct, newListino, date: null };
  const target = new Date(dateStr);
  if (Number.isNaN(target.getTime())) return { state: "set", days: null, pct, newListino, date: null };
  target.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = Math.round((target - today) / (1000 * 60 * 60 * 24));
  if (days === 0) return { state: "today", days: 0, pct, newListino, date: target };
  if (days > 0 && days <= 30) return { state: "imminent", days, pct, newListino, date: target };
  if (days > 30) return { state: "future", days, pct, newListino, date: target };
  if (days < 0 && days >= -7) return { state: "active", days: -days, pct, newListino, date: target };
  return { state: "past", days: -days, pct, newListino, date: target };
}

// Listino "scaduto": ultimo file caricato da più di 2 mesi (60 giorni) → da verificare.
// La proroga (listino_reminder_snooze_until) sospende l'alert fino alla data indicata.
const ASSETS_STALE_MS = 60 * 24 * 60 * 60 * 1000;
function isAssetsStale(s) {
  if (!s?.last_asset_at) return false;
  const snoozeRaw = s?.listino_reminder_snooze_until;
  if (snoozeRaw) {
    const snooze = new Date(snoozeRaw);
    if (!Number.isNaN(snooze.getTime()) && snooze.getTime() > Date.now()) return false;
  }
  const dt = new Date(s.last_asset_at);
  if (Number.isNaN(dt.getTime())) return false;
  return Date.now() - dt.getTime() > ASSETS_STALE_MS;
}

/**
 * Calcola lo stato del "termine ordine per consegne" del fornitore.
 * Stati:
 *  - "today": data == oggi → badge rosso "scade oggi"
 *  - "imminent": entro 15gg futuri → badge ambra pulsante "tra Ngg"
 *  - "future": oltre 15gg → badge ambra normale "dal DD/MM/YYYY"
 *  - "past": scaduto → badge grigio "scaduto da Ngg"
 */
function getOrderDeadlineAlert(supplier) {
  const dateStr = supplier?.order_deadline_date || "";
  if (!dateStr) return null;
  const target = new Date(dateStr);
  if (Number.isNaN(target.getTime())) return null;
  target.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = Math.round((target - today) / (1000 * 60 * 60 * 24));
  if (days === 0) return { state: "today", days: 0, date: target };
  if (days > 0 && days <= 15) return { state: "imminent", days, date: target };
  if (days > 15) return { state: "future", days, date: target };
  return { state: "past", days: -days, date: target };
}

// 3-slot cascading discount input. value: number[] up to 3 entries.
function DiscountChainInput({ value = [], onChange, testidPrefix, disabled }) {
  const arr = [value[0] ?? "", value[1] ?? "", value[2] ?? ""];
  function setAt(i, v) {
    const next = [...arr];
    next[i] = v;
    const cleaned = next.map((x) => (x === "" || x === null ? 0 : Number(x))).map((n) => (Number.isFinite(n) ? n : 0));
    onChange(cleaned);
  }
  const eff = cascadePct(arr.map((v) => Number(v) || 0));
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {[0, 1, 2].map((i) => (
        <React.Fragment key={i}>
          <input
            type="number"
            min={0}
            max={99}
            step="0.5"
            disabled={disabled}
            value={arr[i] === 0 ? "" : arr[i]}
            placeholder={i === 0 ? "50" : i === 1 ? "10" : "10"}
            onChange={(e) => setAt(i, e.target.value)}
            className="w-16 border border-ink/20 px-2 py-2 rounded-sm bg-white text-sm text-center focus:outline-none focus:border-ink"
            data-testid={`${testidPrefix}-${i}`}
          />
          {i < 2 && <span className="text-slate1 text-sm select-none">+</span>}
        </React.Fragment>
      ))}
      <span className="text-[10px] tracking-[0.15em] uppercase text-slate1 ml-1">
        {eff > 0 ? <>= <span className="text-ink font-medium">{eff}%</span></> : "= 0%"}
      </span>
    </div>
  );
}

// Autocomplete per selezionare uno studio/trader registrato: digiti e i match
// (nome, studio, email) compaiono subito cliccabili, senza aprire un menu a tendina.
function StudioTraderCombobox({ options, value, onChange, testidPrefix = "sup-discount-studio" }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const selected = options.find((o) => o.id === value) || null;

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  useEffect(() => { if (!value) setQuery(""); }, [value]);

  const label = (o) =>
    `${o.account_type === "trader" ? "[TRADER] " : o.account_type === "real_estate" ? "[RE] " : ""}${o.studio || o.name || o.email}`;

  const q = query.trim().toLowerCase();
  const filtered = !q
    ? options
    : options.filter((o) => [o.studio, o.name, o.email, o.account_type].some((v) => (v || "").toLowerCase().includes(q)));

  const badge = (t) =>
    t === "trader" ? ["Trader", "bg-amber-100 text-amber-800"]
      : t === "real_estate" ? ["RE", "bg-sky-100 text-sky-800"]
        : ["Studio", "bg-emerald-100 text-emerald-800"];

  if (selected) {
    return (
      <div ref={wrapRef} className="relative">
        <div className="w-full border border-ink/20 px-3 py-2 rounded-sm bg-white text-sm flex items-center justify-between gap-2" data-testid={`${testidPrefix}-selected`}>
          <span className="truncate"><span className="font-medium text-ink">{label(selected)}</span> <span className="text-slate1">({selected.email})</span></span>
          <button type="button" onClick={() => onChange("")} className="text-slate1 hover:text-rose-700 shrink-0" data-testid={`${testidPrefix}-clear`} title="Cambia selezione">
            <X size={14} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div ref={wrapRef} className="relative">
      <div className="relative">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate1 pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && filtered.length > 0) { e.preventDefault(); onChange(filtered[0].id); setOpen(false); }
            if (e.key === "Escape") setOpen(false);
          }}
          placeholder="Cerca studio o trader (nome, studio, email)…"
          className="w-full border border-ink/20 pl-8 pr-3 py-2 rounded-sm bg-white text-sm focus:outline-none focus:border-ink"
          data-testid={`${testidPrefix}-search`}
          autoComplete="off"
        />
      </div>
      {open && (
        <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-ink/15 rounded-sm shadow-lg max-h-64 overflow-y-auto" data-testid={`${testidPrefix}-results`}>
          {filtered.length === 0 ? (
            <div className="px-3 py-3 text-xs text-slate1 italic" data-testid={`${testidPrefix}-empty`}>
              {options.length === 0
                ? "Tutti gli studi/trader registrati hanno già uno sconto."
                : <>Nessuno studio/trader <strong>registrato</strong> corrisponde a «{query}». Gli sconti si applicano solo ad account con login sulla piattaforma.</>}
            </div>
          ) : (
            filtered.map((o) => {
              const [bl, cls] = badge(o.account_type);
              return (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => { onChange(o.id); setOpen(false); }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-cream2/70 flex items-center gap-2 border-b border-ink/5 last:border-0"
                  data-testid={`${testidPrefix}-opt-${o.id}`}
                >
                  <span className={`text-[9px] tracking-wider uppercase px-1.5 py-0.5 rounded-sm shrink-0 ${cls}`}>{bl}</span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-ink">{o.studio || o.name || o.email}</span>
                    <span className="block truncate text-[11px] text-slate1">{o.email}</span>
                  </span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}


// Solo i LISTINI hanno una "scadenza": vanno verificati se caricati da più di
// 2 mesi (60 giorni). Gli altri asset (comunicazioni, file 3D, ecc.) NON
// scadono mai.
function isStale(asset) {
  if (!asset || asset.kind !== "listino") return false;
  const createdAt = asset.created_at;
  if (!createdAt) return false;
  const t = new Date(createdAt).getTime();
  if (isNaN(t)) return false;
  return Date.now() - t > 1000 * 60 * 60 * 24 * 60;
}

export default function AdminAssets() {
  const [suppliers, setSuppliers] = useState([]);
  const [supLoading, setSupLoading] = useState(true);
  const [form, setForm] = useState(emptySupplier);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(null); // supplier currently open
  const [assets, setAssets] = useState([]);
  // Ref sempre sincronizzato con `assets`, per dare a useFileUpload un accesso stabile
  // ai nomi file già caricati senza dover rigenerare la callback ad ogni render.
  const assetsRef = useRef([]);
  useEffect(() => { assetsRef.current = assets; }, [assets]);
  // Filtro di ricerca sugli asset (compare quando ci sono >10 asset).
  const [assetQuery, setAssetQuery] = useState("");
  // Selezione multipla per operazioni in blocco (es. "sposta tutti i catalogo in listino").
  const [selectedAssetIds, setSelectedAssetIds] = useState(() => new Set());
  // Target kind nel selettore della bulk-action bar (default: "listino").
  const [bulkTargetKind, setBulkTargetKind] = useState("listino");
  // Flag che indica se l'operazione bulk è in corso (per disabilitare i bottoni).
  const [bulkBusy, setBulkBusy] = useState(false);
  // Modale per generare/gestire i link condivisi 48h verso clienti esterni.
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [extOpen, setExtOpen] = useState(false);
  const [extForm, setExtForm] = useState({ kind: "catalogo", title: "", external_url: "" });
  const [gdriveOpen, setGdriveOpen] = useState(false);
  const [gdriveForm, setGdriveForm] = useState({ kind: "cad_3d", title: "", folder_url: "" });
  const [gdriveBusy, setGdriveBusy] = useState(false);
  // Verifica integrità storage per supplier: {brokenIds: Set, summary: {checked, ok, broken}}
  const [integrityBusy, setIntegrityBusy] = useState(false);
  const [integrityBrokenIds, setIntegrityBrokenIds] = useState(() => new Set());
  const [integritySummary, setIntegritySummary] = useState(null);
  const [kind, setKind] = useState("catalogo");
  const [supQuery, setSupQuery] = useState("");
  // Filtro speciale attivabile cliccando i banner aggregati. Quando attivo, restringe la lista
  // ai fornitori che soddisfano la condizione (es. solo quelli con aumento imminente).
  // null | "increase_imminent" | "deadline_imminent"
  const [activeFilter, setActiveFilter] = useState(null);
  // Caricamento progressivo (lazy): la lista fornitori può essere molto lunga,
  // quindi renderizziamo a blocchi e aggiungiamo altri elementi scrollando.
  const SUP_PAGE = 25;
  const [supVisible, setSupVisible] = useState(SUP_PAGE);
  const [agentDirectoryOpen, setAgentDirectoryOpen] = useState(false);
  // Rubrica agenti per autocomplete in "Nuovo fornitore"
  const [agentDirectory, setAgentDirectory] = useState([]);
  const [newAgentSuggestOpen, setNewAgentSuggestOpen] = useState(false);

  useEffect(() => {
    api.get("/admin/supplier-agents").then((r) => setAgentDirectory(r.data || [])).catch(() => {});
  }, []);

  // Lista fornitori filtrata (nome/descrizione + filtro attivo), memoizzata.
  const filteredSuppliers = useMemo(() => {
    const qLower = supQuery.toLowerCase();
    return suppliers
      .filter((s) => !supQuery || s.name.toLowerCase().includes(qLower) || (s.description || "").toLowerCase().includes(qLower))
      .filter((s) => {
        if (activeFilter === "deadline_imminent") {
          const od = getOrderDeadlineAlert(s);
          return od && (od.state === "today" || od.state === "imminent");
        }
        if (activeFilter === "increase_imminent") {
          const ia = getIncreaseAlert(s);
          return ia && (ia.state === "today" || ia.state === "imminent");
        }
        if (activeFilter === "assets_stale") return isAssetsStale(s);
        return true;
      });
  }, [suppliers, supQuery, activeFilter]);

  // Reset del blocco visibile quando cambia ricerca/filtro/elenco.
  useEffect(() => { setSupVisible(SUP_PAGE); }, [supQuery, activeFilter, suppliers.length]);

  const visibleSuppliers = useMemo(() => filteredSuppliers.slice(0, supVisible), [filteredSuppliers, supVisible]);

  // Avanzamento AUTOMATICO del rendering progressivo: appena mostrati i primi 25,
  // continuiamo a rivelare altri blocchi da soli (senza click) finché non sono
  // tutti renderizzati. La UI resta reattiva e la ricerca funziona subito su
  // TUTTI i fornitori già scaricati dal backend.
  useEffect(() => {
    if (supVisible >= filteredSuppliers.length) return;
    const t = setTimeout(() => setSupVisible((v) => v + SUP_PAGE), 120);
    return () => clearTimeout(t);
  }, [supVisible, filteredSuppliers.length]);

  const newAgentMatches = useMemo(() => {
    const q = (form.agent_name || "").trim().toLowerCase();
    if (!q || q.length < 1) return [];
    return agentDirectory
      .filter((a) => a.name.toLowerCase().includes(q) || (a.email || "").toLowerCase().includes(q))
      .slice(0, 6);
  }, [form.agent_name, agentDirectory]);

  // Verifica: email digitata corrisponde a un agente già in rubrica con nome diverso?
  // Restituisce l'agente esistente o null.
  const emailConflictAgent = useMemo(() => {
    const email = (form.agent_email || "").trim().toLowerCase();
    const name = (form.agent_name || "").trim().toLowerCase();
    if (!email) return null;
    return agentDirectory.find((a) => {
      const aEmail = (a.email || "").toLowerCase();
      const aName = (a.name || "").toLowerCase();
      return aEmail === email && aName !== name;
    }) || null;
  }, [form.agent_email, form.agent_name, agentDirectory]);

  function applyNewAgent(a) {
    setForm((prev) => ({ ...prev, agent_name: a.name, agent_email: a.email, agent_phone: a.phone }));
    setNewAgentSuggestOpen(false);
  }

  // Apri il drawer di un fornitore dal nome (callback dalla rubrica agenti).
  function jumpToSupplier(supplierName) {
    const sup = suppliers.find((s) => s.name === supplierName);
    if (sup) setOpen(sup);
  }
  // Per-studio discounts
  const [studios, setStudios] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [selStudio, setSelStudio] = useState("");
  const [selChain, setSelChain] = useState([]);
  const [selAdminNote, setSelAdminNote] = useState("");
  const [editNote, setEditNote] = useState({}); // { user_id: noteString } for inline editing
  // Preview modal
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  // Magic Search: progress live per asset in indexing
  const [indexProgress, setIndexProgress] = useState({});
  const [progressPolled, setProgressPolled] = useState(false);
  // Magic Search: riepilogo per-fornitore (dot di stato nella lista)
  const [supIndexSummary, setSupIndexSummary] = useState({});
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/admin/suppliers/indexing-summary");
        if (!cancelled) setSupIndexSummary(data || {});
      } catch {
        // ignore
      }
    };
    tick();
    const t = setInterval(tick, 15000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);
  useEffect(() => {
    // Poll progress se almeno un asset è in indexing
    const someIndexing = assets.some((a) => a.indexed_status === "indexing");
    if (!someIndexing) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/admin/assets/indexing-progress");
        if (!cancelled) {
          setIndexProgress(data.in_progress || {});
          setProgressPolled(true);
        }
      } catch {
        // ignore transient errors
      }
    };
    tick();
    const t = setInterval(tick, 2000);
    return () => { cancelled = true; clearInterval(t); };
  }, [assets]);

  // Auto-refresh assets list every 8s while any asset is indexing (per cogliere transizione → indexed)
  useEffect(() => {
    if (!open) return undefined;
    const someIndexing = assets.some((a) => a.indexed_status === "indexing");
    if (!someIndexing) return undefined;
    const t = setInterval(() => loadAssets(open.id), 8000);
    return () => clearInterval(t);
  }, [assets, open?.id]);
  // Discount requests inbox
  const [requests, setRequests] = useState([]);
  const [requestsOpen, setRequestsOpen] = useState(false);
  const [reqAnswerChain, setReqAnswerChain] = useState({});
  const [reqAnswerNote, setReqAnswerNote] = useState({});

  const reload = useCallback(async () => {
    setSupLoading(true);
    try {
      const { data } = await api.get("/suppliers");
      setSuppliers(data);
      // Keep the open drawer in sync with the latest counts/dates
      setOpen((curr) => {
        if (!curr) return curr;
        const fresh = data.find((s) => s.id === curr.id);
        return fresh ? { ...curr, ...fresh } : curr;
      });
      return data;
    } finally {
      setSupLoading(false);
    }
  }, []);

  // Bozza sollecito listino: nessun invio automatico. Generiamo testo + oggetto +
  // destinatario da usare nel client di posta dell'admin, e permettiamo di
  // scaricare l'immagine della prima pagina del listino da allegare a mano.
  const [draft, setDraft] = useState(null); // { supplier, ...draftData }
  const [draftLoading, setDraftLoading] = useState(null);
  const [draftImgBusy, setDraftImgBusy] = useState(false);
  const [prorogaBusy, setProrogaBusy] = useState(false);
  const [prorogaDate, setProrogaDate] = useState("");

  async function openListinoDraft(s) {
    setDraftLoading(s.id);
    try {
      const { data } = await api.get(`/admin/suppliers/${s.id}/listino-draft`);
      setDraft({ supplier: s, ...data });
      setProrogaDate("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore generazione bozza");
    }
    setDraftLoading(null);
  }

  function openInMailClient() {
    if (!draft) return;
    // encodeURIComponent → spazi come %20 (i client mail non decodificano il '+' nel body/oggetto)
    const q = `subject=${encodeURIComponent(draft.subject || "")}&body=${encodeURIComponent(draft.body || "")}`;
    window.location.href = `mailto:${encodeURIComponent(draft.to || "")}?${q}`;
  }

  async function copyDraftText() {
    if (!draft) return;
    try {
      await navigator.clipboard.writeText(draft.body || "");
      toast.success("Testo copiato negli appunti");
    } catch {
      toast.error("Impossibile copiare il testo");
    }
  }

  async function downloadDraftCovers() {
    if (!draft?.covers_url) return;
    setDraftImgBusy(true);
    try {
      const r = await api.get(draft.covers_url, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `copertine_listini_${(draft.supplier_name || "fornitore").replace(/\s+/g, "_")}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Copertine listini non disponibili");
    }
    setDraftImgBusy(false);
  }

  async function registerProroga() {
    if (!draft) return;
    setProrogaBusy(true);
    try {
      const { data } = await api.post(`/admin/suppliers/${draft.supplier_id}/listino-proroga`, prorogaDate ? { until: prorogaDate } : {});
      const nextIt = new Date(data.snooze_until).toLocaleDateString("it-IT");
      toast.success(`Proroga registrata: prossimo controllo il ${nextIt}`);
      setDraft(null);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore registrazione proroga");
    }
    setProrogaBusy(false);
  }

  const loadRequests = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/discount-requests?status=pending");
      setRequests(data);
    } catch (err) { console.error("[admin-assets] load requests failed", err); }
  }, []);

  // Contesto decisionale per ogni richiesta: condizioni admin + chain peer.
  // Caricato lazily quando il modale richieste è aperto. Mappa rid → context.
  const [reqContext, setReqContext] = useState({});
  const loadRequestContexts = useCallback(async (reqs) => {
    if (!reqs || reqs.length === 0) return;
    const out = {};
    await Promise.all(reqs.map(async (r) => {
      try {
        const { data } = await api.get(`/admin/discount-requests/${r.id}/context`);
        out[r.id] = data;
      } catch (err) {
        out[r.id] = null;
      }
    }));
    setReqContext((prev) => ({ ...prev, ...out }));
  }, []);

  useEffect(() => {
    reload().catch(() => toast.error("Errore caricamento"));
    loadRequests();
  }, [reload, loadRequests]);

  // Quando il modale "Richieste sconto" si apre o le richieste cambiano,
  // pre-fetch in parallelo il context decisionale per ciascuna richiesta.
  const [requestsOpenLoaded, setRequestsOpenLoaded] = useState(false);
  useEffect(() => {
    if (!requestsOpenLoaded) return;
    loadRequestContexts(requests);
  }, [requests, requestsOpenLoaded, loadRequestContexts]);

  async function answerRequest(req) {
    const chain = reqAnswerChain[req.id] || [];
    const valid = chain.filter((v) => Number(v) > 0);
    if (valid.length === 0) { toast.error("Inserisci almeno uno sconto"); return; }
    const effective = cascadePct(chain);
    const note = (reqAnswerNote[req.id] || "").trim();
    try {
      await api.put(`/admin/discount-requests/${req.id}/answer`, { discount_chain: chain, admin_note: note });
      toast.success(`Sconto ${formatChain(chain)} (${effective}%) assegnato a ${req.studio || req.user_email}`);
      setReqAnswerChain((m) => { const { [req.id]: _, ...rest } = m; return rest; });
      setReqAnswerNote((m) => { const { [req.id]: _, ...rest } = m; return rest; });
      await loadRequests();
      if (open && open.id === req.supplier_id) await loadDiscounts(open.id);
    } catch { toast.error("Errore salvataggio sconto"); }
  }

  async function rejectRequest(req) {
    if (!window.confirm(`Rifiutare la richiesta di ${req.studio || req.user_email}?`)) return;
    try {
      await api.delete(`/admin/discount-requests/${req.id}`);
      await loadRequests();
    } catch { toast.error("Errore"); }
  }

  async function loadAssets(sid) {
    const { data } = await api.get(`/suppliers/${sid}/assets`);
    setAssets(data);
    setAssetQuery(""); // Reset filtro quando cambiamo fornitore
    setSelectedAssetIds(new Set()); // Reset selezione su cambio fornitore
  }

  // Toggle singolo asset nella selezione bulk.
  function toggleAssetSelection(aid) {
    setSelectedAssetIds((prev) => {
      const next = new Set(prev);
      if (next.has(aid)) next.delete(aid); else next.add(aid);
      return next;
    });
  }

  // Seleziona o deseleziona tutti gli asset di un gruppo (kind).
  function toggleSelectGroup(items, allSelected) {
    setSelectedAssetIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        items.forEach((a) => next.delete(a.id));
      } else {
        items.forEach((a) => next.add(a.id));
      }
      return next;
    });
  }

  // Esegue il move massivo verso il `bulkTargetKind`.
  async function runBulkMove() {
    if (!open?.id || selectedAssetIds.size === 0) return;
    const ids = Array.from(selectedAssetIds);
    if (!window.confirm(`Spostare ${ids.length} asset in "${KIND_LABEL[bulkTargetKind] || bulkTargetKind}"?`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.patch(`/admin/suppliers/${open.id}/assets-bulk`, {
        asset_ids: ids,
        kind: bulkTargetKind,
      });
      toast.success(`${data.modified || 0} asset spostati in ${KIND_LABEL[bulkTargetKind] || bulkTargetKind}`);
      setSelectedAssetIds(new Set());
      await loadAssets(open.id);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore aggiornamento massivo");
    } finally {
      setBulkBusy(false);
    }
  }

  // "Proroga": aggiorna created_at a oggi (come se i file fossero stati ricaricati).
  async function renewAssets(ids, confirmMsg) {
    if (!open?.id || !ids.length) return;
    if (!window.confirm(confirmMsg)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.patch(`/admin/suppliers/${open.id}/assets-bulk`, {
        asset_ids: ids,
        renew: true,
      });
      toast.success(`${data.modified || 0} file prorogat${(data.modified || 0) === 1 ? "o" : "i"}: data di caricamento aggiornata a oggi`);
      setSelectedAssetIds(new Set());
      await loadAssets(open.id);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore proroga");
    } finally {
      setBulkBusy(false);
    }
  }

  async function loadDiscounts(sid) {
    try {
      const { data } = await api.get(`/admin/suppliers/${sid}/studio-discounts`);
      setDiscounts(data);
    } catch { setDiscounts([]); }
  }

  async function loadStudios() {
    try {
      const { data } = await api.get("/admin/studios");
      setStudios(data);
    } catch { setStudios([]); }
  }

  async function addDiscount() {
    if (!open || !selStudio) { toast.error("Seleziona uno studio o trader"); return; }
    const valid = (selChain || []).filter((v) => Number(v) > 0);
    if (valid.length === 0) { toast.error("Inserisci almeno uno sconto"); return; }
    try {
      await api.put(`/admin/suppliers/${open.id}/studio-discounts/${selStudio}`, { discount_chain: selChain, admin_note: (selAdminNote || "").trim() });
      toast.success("Sconto salvato");
      setSelStudio(""); setSelChain([]); setSelAdminNote("");
      await loadDiscounts(open.id);
    } catch { toast.error("Errore salvataggio sconto"); }
  }

  async function updateDiscountNote(d) {
    if (!open) return;
    const note = (editNote[d.user_id] ?? d.admin_note ?? "").trim();
    try {
      await api.put(`/admin/suppliers/${open.id}/studio-discounts/${d.user_id}`, {
        discount_chain: d.discount_chain,
        admin_note: note,
      });
      toast.success("Nota aggiornata");
      setEditNote((m) => { const { [d.user_id]: _, ...rest } = m; return rest; });
      await loadDiscounts(open.id);
    } catch { toast.error("Errore salvataggio nota"); }
  }

  async function removeDiscount(userId) {
    if (!open) return;
    if (!window.confirm("Rimuovere lo sconto personalizzato per questo studio?")) return;
    try {
      await api.delete(`/admin/suppliers/${open.id}/studio-discounts/${userId}`);
      await loadDiscounts(open.id);
    } catch { toast.error("Errore rimozione"); }
  }

  async function createSupplier(e) {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        lead_time_days: Number(form.lead_time_days || 0),
        discount_pct: Number(form.discount_pct || 0),
        purchase_discount_pct: Number(form.purchase_discount_pct || 0),
        year_end_bonus_budget: Number(form.year_end_bonus_budget || 0),
        price_increase_pct: Number(form.price_increase_pct || 0),
        price_increase_date: form.price_increase_date || "",
        price_increase_new_listino: Boolean(form.price_increase_new_listino),
        order_deadline_date: form.order_deadline_date || "",
      };
      await api.post("/admin/suppliers", payload);
      toast.success("Fornitore creato");
      setForm(emptySupplier);
      setShowCreate(false);
      reload();
    } catch { toast.error("Errore creazione"); }
  }

  async function saveEditing() {
    if (!editing) return;
    try {
      const { id, assets_count, created_at, updated_at, ...rest } = editing;
      const payload = {
        ...rest,
        lead_time_days: Number(rest.lead_time_days || 0),
        discount_pct: Number(rest.discount_pct || 0),
        purchase_discount_pct: Number(rest.purchase_discount_pct || 0),
        year_end_bonus_budget: Number(rest.year_end_bonus_budget || 0),
        price_increase_pct: Number(rest.price_increase_pct || 0),
        price_increase_date: rest.price_increase_date || "",
        price_increase_new_listino: Boolean(rest.price_increase_new_listino),
      };
      await api.put(`/admin/suppliers/${id}`, payload);
      toast.success("Aggiornato");
      setEditing(null);
      reload();
    } catch { toast.error("Errore salvataggio"); }
  }

  async function delSupplier(id, name = "") {
    // Doppia conferma per evitare cancellazioni accidentali. La seconda
    // richiede di DIGITARE il nome del fornitore — protezione robusta contro
    // mis-click (Trash2 è vicino a Pencil/Apri).
    const supplierName = (name || "questo fornitore").trim();
    const ok1 = window.confirm(
      `⚠️  Stai per ELIMINARE il fornitore "${supplierName}" e TUTTI i suoi asset (PDF, link, sconti studio, agenti).\n\n` +
      `Questa operazione è IRREVERSIBILE.\n\n` +
      `Vuoi procedere?`
    );
    if (!ok1) return;
    // Seconda conferma: digitare il nome esatto del fornitore
    const typed = window.prompt(
      `Per confermare la cancellazione DEFINITIVA, digita esattamente il nome del fornitore:\n\n${supplierName}`
    );
    if (typed === null) return; // cancel
    if (typed.trim().toLowerCase() !== supplierName.toLowerCase()) {
      toast.error("Nome non corrispondente. Cancellazione annullata.");
      return;
    }
    try {
      await api.delete(`/admin/suppliers/${id}`);
      toast.success(`Fornitore "${supplierName}" eliminato`);
      reload();
    } catch (e) {
      toast.error("Errore nella cancellazione: " + (e?.response?.data?.detail || e.message));
    }
  }

  // Soglia oltre la quale usiamo upload chunked (per superare il limite proxy nginx ~100MB).
  // Chunk da 1MB: sicuri anche con ingress produzione restrittivi (client_max_body_size stretti)
  // e sotto la soglia standard di 60s di timeout anche su connessioni lente.
  // Storia: 2MB → sporadici blocchi in produzione a <10% (feedback utente 2026-02-25) su file
  // grandi (>50MB); ridotto a 1MB per aumentare la tolleranza. Backend supporta qualsiasi size.
  const CHUNK_THRESHOLD = 1 * 1024 * 1024;
  const CHUNK_SIZE = 1 * 1024 * 1024;
  const CHUNK_MAX_RETRIES = 3; // 3 tentativi totali per chunk (con backoff 1s, 3s)

  /** Estrae un messaggio d'errore leggibile da una response axios/http */
  const _formatChunkError = (err) => {
    const status = err?.response?.status;
    const bodyRaw = err?.response?.data;
    const bodyText = typeof bodyRaw === "string"
      ? bodyRaw
      : (bodyRaw?.detail || bodyRaw?.message || (bodyRaw ? JSON.stringify(bodyRaw) : ""));
    const shortBody = (bodyText || "").slice(0, 200);
    if (status) return `HTTP ${status}${shortBody ? ` · ${shortBody}` : ""}`;
    if (err?.code === "ERR_NETWORK") return "errore di rete (connessione persa o CORS)";
    return err?.message || "errore sconosciuto";
  };

  /**
   * Upload chunked per file grandi.
   * 1) /init → upload_id
   * 2) per ogni chunk POST FormData + onUploadProgress (con retry esponenziale su errori transitori)
   * 3) /complete → asset creato
   * In caso di errore tenta una DELETE di abort (best-effort).
   */
  const uploadChunked = useCallback(async (sid, file, onProgress) => {
    const detected = detectKindFromFilename(file.name, kind);
    const finalKind = (kind === "catalogo" && detected !== "catalogo") ? detected : kind;
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const initResp = await api.post(`/admin/suppliers/${sid}/chunked-upload/init`, {
      filename: file.name,
      total_size: file.size,
      total_chunks: totalChunks,
      content_type: file.type || "application/octet-stream",
      kind: finalKind,
      title: file.name,
    });
    const uploadId = initResp.data.upload_id;
    let cumulativeLoaded = 0;
    try {
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, file.size);
        const chunkSize = end - start;
        const chunkStart = cumulativeLoaded;
        // Retry con backoff su errori transitori (network, 502/503/504, 429).
        let lastErr = null;
        for (let attempt = 1; attempt <= CHUNK_MAX_RETRIES; attempt++) {
          const blob = file.slice(start, end);
          const fd = new FormData();
          fd.append("chunk", blob, `${i}`);
          try {
            await api.post(`/admin/suppliers/${sid}/chunked-upload/${uploadId}/chunk/${i}`, fd, {
              timeout: 0,
              onUploadProgress: (evt) => {
                if (onProgress) {
                  const loaded = chunkStart + (evt.loaded || 0);
                  onProgress(
                    file.size > 0 ? Math.round((loaded / file.size) * 100) : 0,
                    Math.min(loaded, file.size),
                    file.size,
                  );
                }
              },
            });
            lastErr = null;
            break;
          } catch (err) {
            lastErr = err;
            const status = err?.response?.status;
            // 4xx (tranne 408/429) sono errori permanenti → non ritentare
            const transient = !status || status === 408 || status === 429 || (status >= 500 && status < 600);
            if (!transient || attempt === CHUNK_MAX_RETRIES) {
              throw new Error(
                `Chunk ${i + 1}/${totalChunks} (${(chunkSize / 1024).toFixed(0)} KB) fallito: ${_formatChunkError(err)}`,
              );
            }
            const backoffMs = attempt === 1 ? 1000 : 3000;
            await new Promise((res) => setTimeout(res, backoffMs));
          }
        }
        if (lastErr) throw lastErr;
        cumulativeLoaded += chunkSize;
      }
      await api.post(`/admin/suppliers/${sid}/chunked-upload/${uploadId}/complete`, {}, { timeout: 0 });
      if (onProgress) onProgress(100, file.size, file.size);
    } catch (e) {
      // Best-effort cleanup lato server.
      try { await api.delete(`/admin/suppliers/${sid}/chunked-upload/${uploadId}`); } catch { /* noop */ }
      throw e;
    }
  }, [kind]);

  // Upload PARALLELO (hook): caricamento di un singolo file. L'admin "kind" può cambiare durante l'upload, quindi usiamo una callback con il valore corrente di "kind" tramite ref.
  const uploadOneAsset = useCallback(async (sid, file, onProgress) => {
    // File grandi → chunked. File piccoli → upload diretto.
    if (file.size >= CHUNK_THRESHOLD) {
      return uploadChunked(sid, file, onProgress);
    }
    const detected = detectKindFromFilename(file.name, kind);
    const finalKind = (kind === "catalogo" && detected !== "catalogo") ? detected : kind;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", finalKind);
    fd.append("title", file.name);
    await api.post(`/admin/suppliers/${sid}/assets`, fd, {
      timeout: 0,
      onUploadProgress: (evt) => {
        if (onProgress) onProgress(
          evt.total ? Math.round((evt.loaded / evt.total) * 100) : 0,
          evt.loaded || 0,
          evt.total || file.size,
        );
      },
    });
  }, [kind, uploadChunked]);

  // Wrapper hook che ha bisogno del sid corrente (open.id).
  const assetUpload = useFileUpload({
    uploadOne: useCallback(async (file, onProgress) => {
      if (!open?.id) throw new Error("Nessun fornitore aperto");
      await uploadOneAsset(open.id, file, onProgress);
    }, [open?.id, uploadOneAsset]),
    onAfter: useCallback(async () => {
      if (open?.id) {
        await loadAssets(open.id);
        await reload();
      }
    }, [open?.id]),
    // Dedup automatica per nome file (case-insensitive): se un file con lo stesso nome
    // esiste già per questo fornitore, viene SALTATO senza upload. Utile per ricaricare
    // batch grandi senza creare duplicati e senza preoccuparsi di selezionare solo i mancanti.
    getExistingFilenames: useCallback(() => {
      const s = new Set();
      for (const a of (assetsRef.current || [])) {
        if (a?.filename) s.add(String(a.filename).toLowerCase());
      }
      return s;
    }, []),
    successLabel: "file caricato",
    successLabelPlural: "file caricati",
  });

  async function changeAssetKind(sid, aid, newKind) {
    try {
      await api.patch(`/admin/suppliers/${sid}/assets/${aid}`, { kind: newKind });
      toast.success(`Asset spostato in ${KIND_LABEL[newKind]}`);
      await loadAssets(sid);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore aggiornamento");
    }
  }

  async function setAssetArchived(sid, aid, archived) {
    const msg = archived
      ? "Spostare questo listino in \"Vecchi listini\"?\n\nNon sarà più visibile agli utenti, verrà escluso da tutte le ricerche (anche dal Preventivatore) e rimosso dall'indice. Potrai consultarlo solo aprendolo qui nella scheda del fornitore."
      : "Ripristinare questo listino?\n\nTornerà visibile e ricercabile e verrà re-indicizzato automaticamente.";
    if (!window.confirm(msg)) return;
    try {
      const { data } = await api.patch(`/admin/suppliers/${sid}/assets/${aid}`, { archived });
      setAssets((prev) => prev.map((a) => (a.id === aid ? { ...a, ...data } : a)));
      toast.success(archived ? "Spostato in Vecchi listini" : "Ripristinato · re-indicizzazione avviata");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Operazione non riuscita");
    }
  }

  async function toggleExcludePriceIncrease(sid, aid, value) {
    // Aggiorno subito lo stato locale per UX reattiva
    setAssets((prev) => prev.map((a) => a.id === aid ? { ...a, exclude_price_increase: value } : a));
    try {
      await api.patch(`/admin/suppliers/${sid}/assets/${aid}`, { exclude_price_increase: value });
      toast.success(value ? "Aumento listino nascosto per questo file" : "Aumento listino mostrato per questo file");
    } catch (e) {
      // Rollback ottimistico
      setAssets((prev) => prev.map((a) => a.id === aid ? { ...a, exclude_price_increase: !value } : a));
      toast.error(e.response?.data?.detail || "Errore aggiornamento");
    }
  }

  async function addExternal(sid) {
    if (!extForm.title || !extForm.external_url) { toast.error("Titolo e URL richiesti"); return; }
    try {
      await api.post(`/admin/suppliers/${sid}/assets/external`, extForm);
      toast.success("Link aggiunto");
      setExtOpen(false);
      setExtForm({ kind: "catalogo", title: "", external_url: "" });
      await loadAssets(sid);
      await reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  }

  async function addGdriveFolder(sid) {
    if (!gdriveForm.folder_url) { toast.error("URL cartella Drive richiesto"); return; }
    setGdriveBusy(true);
    try {
      const payload = { ...gdriveForm, title: gdriveForm.title || "Cartella Drive" };
      const r = await api.post(`/admin/suppliers/${sid}/assets/gdrive-folder`, payload);
      toast.success(`Cartella indicizzata: ${r.data?.gdrive_files_count || 0} file`);
      setGdriveOpen(false);
      setGdriveForm({ kind: "cad_3d", title: "", folder_url: "" });
      await loadAssets(sid);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore indicizzazione cartella Drive");
    } finally {
      setGdriveBusy(false);
    }
  }

  async function refreshGdrive(aid) {
    try {
      const r = await api.post(`/admin/assets/${aid}/refresh-gdrive`);
      toast.success(`Re-indicizzato: ${r.data?.count || 0} file`);
      if (open?.id) await loadAssets(open.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore re-indicizzazione");
    }
  }

  async function delAsset(sid, aid) {
    if (!window.confirm("Eliminare l'asset?")) return;
    try {
      await api.delete(`/admin/suppliers/${sid}/assets/${aid}`);
      // Optimistic update so the user sees instant feedback
      setAssets((prev) => prev.filter((a) => a.id !== aid));
      setOpen((s) => (s && s.id === sid ? { ...s, assets_count: Math.max(0, (s.assets_count || 1) - 1) } : s));
      toast.success("Asset eliminato");
      await loadAssets(sid);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore eliminazione");
    }
  }

  async function forceReindexAsset(sid, aid) {
    if (!window.confirm("Forzare l'indicizzazione di questo PDF? Per file grandi (>100 MB) può richiedere alcuni minuti e impegnare il server.")) return;
    try {
      const r = await api.post(`/admin/suppliers/${sid}/assets/${aid}/reindex-force`);
      toast.success(`Indicizzazione avviata (${r.data.size_mb} MB). Il file apparirà come indicizzato a breve.`);
      // Aggiorna lo stato locale: marca come "indexing"
      setAssets((prev) => prev.map((a) => a.id === aid ? { ...a, indexed_status: "indexing", index_error: null } : a));
      // Refresh dopo qualche secondo per vedere il risultato
      setTimeout(() => loadAssets(sid), 8000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore indicizzazione");
    }
  }

  async function cancelStuckIndexing(sid, aid) {
    if (!window.confirm("Annullare l'indicizzazione bloccata e riprovare?")) return;
    try {
      // Il reindex-force unsetta lo stato e re-pianifica, anche per asset 'indexing'
      const r = await api.post(`/admin/suppliers/${sid}/assets/${aid}/reindex-force`);
      toast.success(`Indicizzazione riavviata (${r.data.size_mb} MB)`);
      setAssets((prev) => prev.map((a) => a.id === aid ? { ...a, indexed_status: "indexing", index_error: null } : a));
      setTimeout(() => loadAssets(sid), 8000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore annullamento");
    }
  }

  async function replaceAssetFile(sid, aid, fileInputEvent) {
    const file = fileInputEvent.target.files && fileInputEvent.target.files[0];
    if (!file) return;
    fileInputEvent.target.value = ""; // reset per permettere upload dello stesso file di nuovo
    if (!window.confirm(`Sostituire il file binario di questo asset con "${file.name}" (${(file.size/1024/1024).toFixed(2)} MB)? I metadati (titolo, kind, sconti) restano invariati. L'indice Magic Search verrà ricostruito.`)) return;
    const toastId = toast.loading(`Caricamento ${file.name}…`);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/admin/suppliers/${sid}/assets/${aid}/replace-file`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 600000,
      });
      toast.success(`File sostituito (${(r.data.size/1024/1024).toFixed(2)} MB). Re-indicizzazione in corso.`, { id: toastId });
      // Se l'asset era marcato come rotto, rimuovilo dal set
      setIntegrityBrokenIds((prev) => {
        if (!prev.has(aid)) return prev;
        const next = new Set(prev);
        next.delete(aid);
        return next;
      });
      await loadAssets(sid);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sostituzione fallita", { id: toastId });
    }
  }

  async function verifySupplierIntegrity(sid) {
    setIntegrityBusy(true);
    const toastId = toast.loading("Verifica integrità file su storage…");
    try {
      const r = await api.post(`/admin/suppliers/${sid}/assets/verify-integrity`);
      const { checked, ok, broken, broken_list } = r.data;
      const brokenSet = new Set((broken_list || []).map((b) => b.asset_id));
      setIntegrityBrokenIds(brokenSet);
      setIntegritySummary({ checked, ok, broken });
      if (broken === 0) {
        toast.success(`Tutti i ${checked} file OK su storage.`, { id: toastId });
      } else {
        toast.error(`${broken}/${checked} file ROTTI. Usa 🔁 Sostituisci per ripararli.`, { id: toastId, duration: 7000 });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verifica integrità fallita", { id: toastId });
    } finally {
      setIntegrityBusy(false);
    }
  }

  async function forceRedownloadSupplier(sid) {
    if (!window.confirm("Forzare il re-download di TUTTI i file di questo fornitore?\n\nUsa questa funzione quando la verifica integrità dice OK ma i preview continuano a fallire — può essere cache server-side corrotta. I file vengono ri-scaricati al prossimo click.")) return;
    const toastId = toast.loading("Invalidazione cache di preview…");
    try {
      const r = await api.post(`/admin/suppliers/${sid}/assets/invalidate-cache`);
      toast.success(`Cache invalidata: ${r.data.invalidated}/${r.data.checked} file. I preview successivi ri-scaricheranno da storage.`, { id: toastId, duration: 6000 });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalidazione cache fallita", { id: toastId });
    }
  }

  async function downloadAsset(a) {
    try {
      if (a.content_type === "external/url" || a.content_type === "gdrive/folder") {
        const { data } = await api.get(`/assets/${a.id}/download`);
        if (data.redirect) window.open(data.redirect, "_blank");
        return;
      }
      const r = await api.get(`/assets/${a.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const link = document.createElement("a");
      link.href = url; link.download = a.filename; link.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download fallito"); }
  }

  function isPreviewable(a) {
    if (a.content_type === "external/url") return false;
    const ct = (a.content_type || "").toLowerCase();
    const fn = (a.filename || "").toLowerCase();
    if (ct === "application/pdf" || fn.endsWith(".pdf")) return true;
    if (ct.startsWith("image/")) return true;
    return false;
  }

  async function previewAsset(a, page, searchTerm) {
    if (!isPreviewable(a)) return;
    const ct = (a.content_type || "").toLowerCase();
    const isPdf = ct === "application/pdf" || (a.filename || "").toLowerCase().endsWith(".pdf");

    // PDF → apri sempre nel viewer custom (/pdfjs/viewer.html) per garantire
    // page-jump e highlight cross-browser anche con extension Adobe installate.
    if (isPdf) {
      const fileUrl = `${API}/assets/${a.id}/preview`;
      const qp = new URLSearchParams();
      qp.set("file", fileUrl);
      if (page && Number.isFinite(Number(page))) qp.set("page", String(page));
      if (searchTerm && typeof searchTerm === "string") {
        const firstToken = searchTerm.trim().split(/\s+/)[0];
        if (firstToken) qp.set("search", firstToken);
      }
      // AdminAssets è admin-only → sempre admin=1 (mostra "Scarica" nel viewer)
      qp.set("admin", "1");
      qp.set("from", "admin");
      window.open(`/pdfjs/viewer.html?${qp.toString()}`, "_blank");
      return;
    }

    let pdfTab = null;
    if (isPdf) {
      pdfTab = window.open("about:blank", "_blank");
      if (!pdfTab) {
        toast.error("Il browser ha bloccato l'apertura. Consenti i pop-up.");
        return;
      }
    }
    setPreviewLoading(true);
    try {
      const r = await api.get(`/assets/${a.id}/preview`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: a.content_type || "application/pdf" });
      const url = URL.createObjectURL(blob);
      if (isPdf && pdfTab) {
        pdfTab.location.href = url;
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } else {
        setPreview({ url, name: a.title || a.filename, isPdf: false, isImage: ct.startsWith("image/") });
      }
    } catch {
      if (pdfTab) {
        try { pdfTab.close(); } catch (_) { /* ignore */ }
      }
      toast.error("Anteprima non disponibile");
    }
    finally { setPreviewLoading(false); }
  }

  // Handlers per Magic Search dentro la scheda admin.
  // `term` propaga il termine cercato così che il PDF viewer evidenzi la parola.
  const handleMagicOpenAtPage = (result, page, term) => {
    if (result.is_external && result.external_url) {
      window.open(result.external_url, "_blank");
      return;
    }
    if (result.is_gdrive_folder && result.external_url) {
      window.open(result.external_url, "_blank");
      return;
    }
    const asset = assets.find((a) => a.id === result.asset_id);
    if (asset) previewAsset(asset, page, term);
  };
  const handleMagicOpenFile = (result, term) => handleMagicOpenAtPage(result, undefined, term);

  function closePreview() {
    if (preview?.url) URL.revokeObjectURL(preview.url);
    setPreview(null);
  }

  const groupedAssets = useMemo(() => {
    const buckets = {};
    ASSET_KINDS.forEach((g) => { buckets[g.value] = []; });
    const archivedItems = [];
    const q = assetQuery.trim().toLowerCase();
    assets.forEach((a) => {
      // Filtro testuale su filename + title + description (case-insensitive).
      if (q) {
        const hay = `${a.filename || ""} ${a.title || ""} ${a.description || ""}`.toLowerCase();
        if (!hay.includes(q)) return;
      }
      // I "Vecchi listini" archiviati vanno in un gruppo dedicato (solo admin, in fondo).
      if (a.archived) { archivedItems.push(a); return; }
      const key = buckets[a.kind] !== undefined ? a.kind : "altro";
      buckets[key].push(a);
    });
    const groups = ASSET_KINDS.map((g) => ({ ...g, items: buckets[g.value] })).filter((g) => g.items.length > 0);
    if (archivedItems.length > 0) {
      groups.push({ value: "vecchi_listini", label: "Vecchi listini", adminOnly: true, archivedGroup: true, items: archivedItems });
    }
    return groups;
  }, [assets, assetQuery]);

  // Numero totale di asset visibili dopo il filtro (per badge "X / Y file").
  const filteredAssetsCount = useMemo(
    () => groupedAssets.reduce((s, g) => s + g.items.length, 0),
    [groupedAssets],
  );

  return (
    <div className="space-y-6" data-testid="admin-assets">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[11px] tracking-[0.2em] uppercase text-slate1">Gestione asset & sconti</div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { setForm(emptySupplier); setShowCreate(true); }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-ink text-cream rounded-sm text-[11px] tracking-[0.2em] uppercase hover:bg-ink/90"
            data-testid="admin-sup-create-btn"
          >
            <Plus size={14} /> Nuovo fornitore
          </button>
          <button
            onClick={() => { setRequestsOpen(true); setRequestsOpenLoaded(true); loadRequests(); }}
            className={`relative inline-flex items-center gap-2 px-4 py-2 rounded-sm text-[11px] tracking-[0.2em] uppercase transition-colors ${requests.length > 0 ? "bg-gold text-cream" : "border border-ink/20 text-slate1 hover:bg-ink hover:text-cream hover:border-ink"}`}
            data-testid="discount-requests-btn"
          >
            <Bell size={14} /> Richieste sconto
            {requests.length > 0 && (
              <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-[20px] px-1 bg-cream text-ink text-[10px] rounded-full">
                {requests.length}
              </span>
            )}
          </button>
          <AdminMagicRequests />
        </div>
      </div>

      <MagicSearchStatusPanel />

      <ListinoDriveVerify suppliers={suppliers} />


      {showCreate && (
        <div className="fixed inset-0 z-50 bg-ink/50 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setShowCreate(false)} data-testid="admin-sup-create-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-cream max-w-3xl w-full my-8 rounded-sm shadow-xl">
            <div className="flex items-center justify-between p-5 border-b border-ink/10">
              <div className="font-display text-xl text-ink">Nuovo fornitore</div>
              <button onClick={() => setShowCreate(false)} className="text-slate1 hover:text-ink" data-testid="admin-sup-create-close" aria-label="Chiudi">
                <X size={18} />
              </button>
            </div>
            <form onSubmit={createSupplier} className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="supplier-form">
        <div>
          <label className="text-[10px] tracking-[0.2em] uppercase text-slate1">Nome</label>
          <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 w-full border border-ink/20 px-3 py-2 rounded-sm bg-cream" />
        </div>
        <div>
          <label className="text-[10px] tracking-[0.2em] uppercase text-slate1">Tempi produzione (giorni)</label>
          <input type="number" min={0} value={form.lead_time_days} onChange={(e) => setForm({ ...form, lead_time_days: e.target.value })} className="mt-1 w-full border border-ink/20 px-3 py-2 rounded-sm bg-cream" />
        </div>
        <div>
          <label className="text-[10px] tracking-[0.2em] uppercase text-slate1 block mb-1">Scontistica riservata studio</label>
          <DiscountChainInput
            value={form.discount_chain && form.discount_chain.length ? form.discount_chain : (form.discount_pct ? [form.discount_pct, 0, 0] : [])}
            onChange={(arr) => setForm({ ...form, discount_chain: arr })}
            testidPrefix="sup-new-discount-chain"
          />
        </div>
        <div className="md:col-span-3">
          <label className="text-[10px] tracking-[0.2em] uppercase text-slate1">Descrizione</label>
          <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 w-full border border-ink/20 px-3 py-2 rounded-sm bg-cream" />
        </div>
        <div className="md:col-span-2">
          <label className="text-[10px] tracking-[0.2em] uppercase text-slate1">Sito web</label>
          <input
            type="url"
            value={form.website}
            onChange={(e) => setForm({ ...form, website: e.target.value })}
            placeholder="https://www.fornitore.com"
            className="mt-1 w-full border border-ink/20 px-3 py-2 rounded-sm bg-cream"
          />
        </div>

        <div className="md:col-span-3 mt-2 pt-4 border-t border-ink/10">
          <div className="text-[10px] tracking-[0.2em] uppercase text-gold inline-flex items-center gap-1.5 mb-1">
            <Lock size={10} /> Solo admin · informazioni commerciali
          </div>
        </div>
        <div>
          <label className="text-[10px] tracking-[0.2em] uppercase text-slate1 block mb-1">Sconto acquisto</label>
          <DiscountChainInput
            value={form.purchase_discount_chain && form.purchase_discount_chain.length ? form.purchase_discount_chain : (form.purchase_discount_pct ? [form.purchase_discount_pct, 0, 0] : [])}
            onChange={(arr) => setForm({ ...form, purchase_discount_chain: arr })}
            testidPrefix="sup-new-purchase-chain"
          />
        </div>
        <div>
          <label className="text-[10px] tracking-[0.2em] uppercase text-slate1">Pagamento</label>
          <input value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} placeholder="es. 30/60 gg fm" className="mt-1 w-full border border-ink/20 px-3 py-2 rounded-sm bg-cream" />
        </div>
        <div>
          <label className="text-[10px] tracking-[0.2em] uppercase text-slate1">Budget premio fine anno (€)</label>
          <input type="number" min={0} step="100" value={form.year_end_bonus_budget} onChange={(e) => setForm({ ...form, year_end_bonus_budget: e.target.value })} className="mt-1 w-full border border-ink/20 px-3 py-2 rounded-sm bg-cream" />
        </div>
        <div>
          <label className="text-[10px] tracking-[0.2em] uppercase text-slate1">Aumento listino (%)</label>
          <input type="number" min={0} step="0.5" value={form.price_increase_pct} onChange={(e) => setForm({ ...form, price_increase_pct: e.target.value })} placeholder="es. 5" disabled={Boolean(form.price_increase_new_listino)} className={`mt-1 w-full border border-ink/20 px-3 py-2 rounded-sm ${form.price_increase_new_listino ? "bg-cream/50 text-slate-400 cursor-not-allowed" : "bg-cream"}`} data-testid="sup-new-increase-pct" />
          <label className="mt-1.5 flex items-center gap-1.5 text-[11px] text-slate1 cursor-pointer select-none">
            <input type="checkbox" checked={Boolean(form.price_increase_new_listino)} onChange={(e) => setForm({ ...form, price_increase_new_listino: e.target.checked, ...(e.target.checked ? { price_increase_pct: 0 } : {}) })} data-testid="sup-new-increase-newlistino" />
            Nuovo listino (senza %)
          </label>
        </div>
        <div>
          <label className="text-[10px] tracking-[0.2em] uppercase text-slate1">Decorrenza aumento</label>
          <input type="date" value={form.price_increase_date} onChange={(e) => setForm({ ...form, price_increase_date: e.target.value })} className="mt-1 w-full border border-ink/20 px-3 py-2 rounded-sm bg-cream" data-testid="sup-new-increase-date" />
        </div>
        <div className="relative">
          <label className="text-[10px] tracking-[0.2em] uppercase text-slate1 block mb-1">
            Agente
            {agentDirectory.length > 0 && (
              <span className="ml-2 text-[9px] normal-case tracking-normal text-slate1">· {agentDirectory.length} in rubrica</span>
            )}
          </label>
          <input
            value={form.agent_name}
            onChange={(e) => { setForm({ ...form, agent_name: e.target.value }); setNewAgentSuggestOpen(true); }}
            onFocus={() => setNewAgentSuggestOpen(true)}
            onBlur={() => setTimeout(() => setNewAgentSuggestOpen(false), 150)}
            autoComplete="off"
            placeholder="Digita per riutilizzare un agente esistente"
            className="w-full border border-ink/20 px-3 py-2 rounded-sm bg-cream"
            data-testid="sup-new-agent-name"
          />
          {newAgentSuggestOpen && newAgentMatches.length > 0 && (
            <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-ink/20 rounded-sm shadow-lg max-h-60 overflow-auto" data-testid="sup-new-agent-suggestions">
              {newAgentMatches.map((a, idx) => (
                <button
                  type="button"
                  key={`${a.name}-${a.email}-${idx}`}
                  onMouseDown={(e) => { e.preventDefault(); applyNewAgent(a); }}
                  className="w-full text-left px-3 py-2 hover:bg-cream2 border-b border-ink/10 last:border-b-0"
                  data-testid={`sup-new-agent-sugg-${idx}`}
                >
                  <div className="text-sm text-ink font-medium">{a.name}</div>
                  <div className="text-[10px] text-slate1 flex flex-wrap gap-x-3 mt-0.5">
                    {a.email && <span>📧 {a.email}</span>}
                    {a.phone && <span>📞 {a.phone}</span>}
                  </div>
                  {a.supplier_names?.length > 0 && (
                    <div className="text-[9px] tracking-[0.15em] uppercase text-slate1 mt-0.5">
                      Già su: {a.supplier_names.slice(0, 3).join(", ")}{a.supplier_names.length > 3 ? "…" : ""}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
        <div>
          <label className="text-[10px] tracking-[0.2em] uppercase text-slate1 block mb-1">Telefono agente</label>
          <input value={form.agent_phone} onChange={(e) => setForm({ ...form, agent_phone: e.target.value })} className="w-full border border-ink/20 px-3 py-2 rounded-sm bg-cream" data-testid="sup-new-agent-phone" />
        </div>
        <div>
          <label className="text-[10px] tracking-[0.2em] uppercase text-slate1 block mb-1">Email agente</label>
          <input type="email" autoComplete="off" value={form.agent_email} onChange={(e) => setForm({ ...form, agent_email: e.target.value })} className={`w-full border ${emailConflictAgent ? "border-rose-500 ring-2 ring-rose-200" : "border-ink/20"} px-3 py-2 rounded-sm bg-cream`} data-testid="sup-new-agent-email" />
          {emailConflictAgent && (
            <div className="mt-1.5 text-[10px] text-rose-700 leading-snug" data-testid="sup-new-agent-email-conflict">
              ⚠ Questa email è già di <strong>{emailConflictAgent.name}</strong>
              {emailConflictAgent.supplier_names?.length > 0 && (
                <span> (su {emailConflictAgent.supplier_names.slice(0, 2).join(", ")}{emailConflictAgent.supplier_names.length > 2 ? "…" : ""})</span>
              )}.
              <button
                type="button"
                onClick={() => setForm({ ...form, agent_name: emailConflictAgent.name, agent_phone: emailConflictAgent.phone, agent_email: emailConflictAgent.email })}
                className="ml-2 underline hover:text-rose-900"
                data-testid="sup-new-agent-email-conflict-fix"
              >
                Usa quello esistente
              </button>
            </div>
          )}
        </div>
        <div>
          <label className="text-[10px] tracking-[0.2em] uppercase text-slate1 block mb-1">Referente azienda</label>
          <input value={form.company_contact_name} onChange={(e) => setForm({ ...form, company_contact_name: e.target.value })} className="w-full border border-ink/20 px-3 py-2 rounded-sm bg-cream" data-testid="sup-new-contact-name" />
        </div>
        <div>
          <label className="text-[10px] tracking-[0.2em] uppercase text-slate1 block mb-1">Telefono azienda</label>
          <input type="tel" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} placeholder="+39 …" className="w-full border border-ink/20 px-3 py-2 rounded-sm bg-cream" data-testid="sup-new-contact-phone" />
        </div>
        <div className="md:col-span-2">
          <label className="text-[10px] tracking-[0.2em] uppercase text-slate1 block mb-1">Email ordini</label>
          <input type="email" autoComplete="off" value={form.orders_email} onChange={(e) => setForm({ ...form, orders_email: e.target.value })} placeholder="ordini@fornitore.it" className="w-full border border-ink/20 px-3 py-2 rounded-sm bg-cream" data-testid="sup-new-orders-email" />
        </div>
        <div>
          <label className="text-[10px] tracking-[0.2em] uppercase text-slate1 block mb-1">Termine ordine per consegne</label>
          <input type="date" value={form.order_deadline_date} onChange={(e) => setForm({ ...form, order_deadline_date: e.target.value })} className="w-full border border-ink/20 px-3 py-2 rounded-sm bg-cream" data-testid="sup-new-order-deadline" />
        </div>
        <div className="md:col-span-3">
          <label className="text-[10px] tracking-[0.2em] uppercase text-slate1">Note</label>
          <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1 w-full border border-ink/20 px-3 py-2 rounded-sm bg-cream resize-y" />
        </div>

        <div className="md:col-span-3 flex justify-end gap-2">
          <button type="button" onClick={() => setShowCreate(false)} className="px-5 py-2.5 text-[11px] tracking-[0.2em] uppercase text-slate1 hover:text-ink">Annulla</button>
          <button className="bg-ink text-cream px-5 py-2.5 text-[11px] tracking-[0.2em] uppercase rounded-sm inline-flex items-center gap-2"><Plus size={14} /> Crea fornitore</button>
        </div>
      </form>
          </div>
        </div>
      )}

      <div className="bg-white border border-ink/10 rounded-sm">
        <div className="p-4 border-b border-ink/10 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2.5 flex-wrap">
            <div className="text-[11px] tracking-[0.2em] uppercase text-slate1">Listini ({suppliers.length})</div>
            {(() => {
              const staleCount = suppliers.filter(isAssetsStale).length;
              if (!staleCount) return null;
              return (
                <button
                  type="button"
                  onClick={() => setActiveFilter(activeFilter === "assets_stale" ? null : "assets_stale")}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] tracking-[0.1em] uppercase border border-rose-300 bg-rose-50 text-rose-700 animate-pulse hover:bg-rose-100 transition-colors ${activeFilter === "assets_stale" ? "font-semibold ring-1 ring-rose-400" : ""}`}
                  data-testid="stale-assets-counter"
                  title="Fornitori con listini caricati da più di 2 mesi — clicca per vedere quelli da verificare"
                >
                  <AlertTriangle size={11} /> {staleCount} scadut{staleCount === 1 ? "o" : "i"}
                </button>
              );
            })()}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => setAgentDirectoryOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 border border-ink/20 text-ink rounded-sm text-[11px] tracking-[0.2em] uppercase hover:bg-cream2 transition-colors"
              data-testid="open-agent-directory"
              title="Apri rubrica agenti"
            >
              <BookUser size={13} /> Rubrica agenti
            </button>
            <div className="relative w-full max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate1" size={14} />
              <input
                value={supQuery}
                onChange={(e) => setSupQuery(e.target.value)}
                placeholder="Cerca fornitore..."
                className="w-full pl-9 pr-3 py-2 border border-ink/20 bg-cream rounded-sm text-sm focus:outline-none focus:border-ink"
                data-testid="admin-sup-search"
              />
            </div>
          </div>
        </div>
        {(() => {
          const alerts = suppliers.map((s) => ({ s, a: getIncreaseAlert(s) })).filter((x) => x.a);
          const imminent = alerts.filter((x) => x.a.state === "imminent" || x.a.state === "today");
          const active   = alerts.filter((x) => x.a.state === "active");
          if (imminent.length === 0 && active.length === 0) return null;
          return (
            <div className="px-4 py-3 border-b border-ink/10 bg-gradient-to-r from-amber-50 to-rose-50 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[11px]" data-testid="sup-increase-alert-banner">
              <AlertTriangle size={13} className="text-amber-700 shrink-0" />
              {imminent.length > 0 && (
                <button
                  type="button"
                  onClick={() => setActiveFilter(activeFilter === "increase_imminent" ? null : "increase_imminent")}
                  className={`text-amber-900 hover:underline ${activeFilter === "increase_imminent" ? "font-semibold underline" : ""}`}
                  data-testid="sup-increase-alert-imminent"
                  title="Mostra solo i fornitori con aumento imminente"
                >
                  <span className="font-semibold">{imminent.length}</span> {imminent.length === 1 ? "fornitore" : "fornitori"} con aumento listino <span className="font-medium">entro 30 giorni</span>
                </button>
              )}
              {active.length > 0 && (
                <span className="text-rose-800" data-testid="sup-increase-alert-active">
                  <span className="font-semibold">{active.length}</span> {active.length === 1 ? "fornitore" : "fornitori"} con aumento <span className="font-medium">appena entrato in vigore</span>
                </span>
              )}
              <span className="text-slate-500 ml-auto text-[10px] tracking-[0.1em] uppercase">Pianifica ordini prima del rincaro</span>
            </div>
          );
        })()}
        {(() => {
          const dl = suppliers.map((s) => ({ s, a: getOrderDeadlineAlert(s) })).filter((x) => x.a);
          const today    = dl.filter((x) => x.a.state === "today");
          const imminent = dl.filter((x) => x.a.state === "imminent");
          if (today.length === 0 && imminent.length === 0) return null;
          const total = today.length + imminent.length;
          return (
            <div className="px-4 py-3 border-b border-ink/10 bg-gradient-to-r from-rose-50 to-blue-50 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[11px]" data-testid="sup-deadline-alert-banner">
              <AlertTriangle size={13} className="text-rose-700 shrink-0" />
              <button
                type="button"
                onClick={() => setActiveFilter(activeFilter === "deadline_imminent" ? null : "deadline_imminent")}
                className={`text-rose-800 hover:underline ${activeFilter === "deadline_imminent" ? "font-semibold underline" : ""}`}
                data-testid="sup-deadline-alert-imminent"
                title="Mostra solo i fornitori con termine ordine vicino"
              >
                <span className="font-semibold">{total}</span> {total === 1 ? "fornitore" : "fornitori"} con <span className="font-medium">termine ordine</span> {today.length > 0 ? <>oggi o <span className="font-medium">entro 15 giorni</span></> : <span className="font-medium">entro 15 giorni</span>}
              </button>
              <span className="text-slate-500 ml-auto text-[10px] tracking-[0.1em] uppercase">Ordina prima della chiusura</span>
            </div>
          );
        })()}
        <div className="divide-y divide-ink/10">
          {activeFilter && (
            <div className="px-4 py-2 bg-ink/5 border-b border-ink/10 flex items-center justify-between text-[11px]" data-testid="sup-active-filter">
              <span className="text-slate1 tracking-[0.15em] uppercase">
                Filtro attivo: {activeFilter === "deadline_imminent" ? "termini ordine entro 15 giorni" : activeFilter === "assets_stale" ? "listini caricati da più di 2 mesi (da verificare)" : "aumenti listino entro 30 giorni"}
              </span>
              <button
                type="button"
                onClick={() => setActiveFilter(null)}
                className="text-ink hover:text-rose-700 inline-flex items-center gap-1 tracking-[0.15em] uppercase"
                data-testid="sup-active-filter-clear"
              >
                <X size={11} /> Rimuovi
              </button>
            </div>
          )}
          {supLoading && suppliers.length === 0 && (
            <div className="p-6 text-sm text-slate1 inline-flex items-center gap-2" data-testid="sup-list-loading">
              <Loader2 size={15} className="animate-spin" /> Caricamento fornitori in corso…
            </div>
          )}
          {!supLoading && suppliers.length === 0 && <div className="p-6 text-sm text-slate1">Nessun fornitore creato.</div>}
          {suppliers.length > 0 && filteredSuppliers.length === 0 && (
            supLoading
              ? <div className="p-6 text-sm text-slate1 inline-flex items-center gap-2" data-testid="sup-search-loading"><Loader2 size={15} className="animate-spin" /> I fornitori sono ancora in caricamento…</div>
              : <div className="p-6 text-sm text-slate1">Nessun fornitore corrisponde ai filtri.</div>
          )}
          {visibleSuppliers
            .map((s) => {
              const sumInfo = supIndexSummary[s.id];
              // Calcola lo stato Magic Search del fornitore
              let dotCls = "bg-slate-300";
              let dotTitle = "Nessun PDF da indicizzare";
              if (sumInfo && sumInfo.total > 0) {
                if (sumInfo.indexing > 0) {
                  dotCls = "bg-amber-500 animate-pulse";
                  dotTitle = `${sumInfo.indexing} file in indicizzazione…`;
                } else if (sumInfo.errors > 0 || sumInfo.skipped > 0) {
                  dotCls = "bg-rose-500";
                  dotTitle = `${sumInfo.errors + sumInfo.skipped} file da indicizzare manualmente (errori/saltati)`;
                } else if (sumInfo.indexed === sumInfo.total) {
                  dotCls = "bg-emerald-500";
                  dotTitle = `Tutti i ${sumInfo.total} PDF indicizzati`;
                } else if (sumInfo.indexed > 0) {
                  dotCls = "bg-amber-400";
                  dotTitle = `${sumInfo.indexed}/${sumInfo.total} PDF indicizzati`;
                } else {
                  dotCls = "bg-slate-400";
                  dotTitle = `Da indicizzare (0/${sumInfo.total})`;
                }
              }
              return (
            <div key={s.id} className="p-4 flex items-start gap-4" data-testid={`sup-${s.id}`}>
              <Folder className="text-slate1 mt-1" size={20} />
              <div className="flex-1 min-w-0">
                <div className="text-ink font-medium flex items-center gap-2">
                  <span
                    className={`inline-block w-2.5 h-2.5 rounded-full shrink-0 ${dotCls}`}
                    title={dotTitle}
                    data-testid={`sup-index-dot-${s.id}`}
                  />
                  <button
                    type="button"
                    onClick={() => { setOpen(s); loadAssets(s.id); loadDiscounts(s.id); loadStudios(); }}
                    className="text-left hover:text-gold hover:underline underline-offset-4 transition-colors cursor-pointer"
                    title="Apri scheda fornitore"
                    data-testid={`sup-open-name-${s.id}`}
                  >
                    {s.name}
                  </button>
                  {sumInfo && sumInfo.total > 0 && (
                    <span className="text-[10px] tracking-[0.15em] uppercase text-slate1 font-normal" data-testid={`sup-index-count-${s.id}`}>
                      · {sumInfo.indexed}/{sumInfo.total} PDF
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate1 mt-0.5">
                  {s.lead_time_days > 0 && <>Lead {s.lead_time_days} gg · </>}
                  {s.discount_pct > 0 && <>Sconto riservato {s.discount_chain && s.discount_chain.length > 1 ? `${formatChain(s.discount_chain)} (${s.discount_pct}%)` : `${s.discount_pct}%`} · </>}
                  {s.assets_count} file
                </div>

                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px]">
                  {s.purchase_discount_pct > 0 && (
                    <span className="inline-flex items-center gap-1 text-gold" data-testid={`sup-purchase-${s.id}`}>
                      <Tag size={11} />
                      Acquisto&nbsp;
                      <span className="font-medium">
                        {s.purchase_discount_chain && s.purchase_discount_chain.length > 1
                          ? `${formatChain(s.purchase_discount_chain)} (${s.purchase_discount_pct}%)`
                          : `${s.purchase_discount_pct}%`}
                      </span>
                    </span>
                  )}
                  {(s.agent_name || s.agent_phone) && (
                    <span className="inline-flex items-center gap-1 text-slate1" data-testid={`sup-agent-${s.id}`}>
                      <UserIcon size={11} />
                      {s.agent_name || "—"}
                      {s.agent_phone && <span className="text-ink/70 ml-1">· {s.agent_phone}</span>}
                    </span>
                  )}
                  {s.agent_email && (
                    <span className="inline-flex items-center gap-1 text-slate1" data-testid={`sup-agent-email-${s.id}`}>
                      <MailQuestion size={11} />
                      <a href={`mailto:${s.agent_email}`} className="text-ink/70 hover:text-ink truncate max-w-[180px]" title={s.agent_email} onClick={(e) => e.stopPropagation()}>{s.agent_email}</a>
                    </span>
                  )}
                  {s.company_contact_name && (
                    <span className="inline-flex items-center gap-1 text-slate1" data-testid={`sup-contact-${s.id}`}>
                      <UserIcon size={11} />
                      <span className="text-ink/70">Ref. {s.company_contact_name}</span>
                    </span>
                  )}
                  {s.orders_email && (
                    <span className="inline-flex items-center gap-1 text-slate1" data-testid={`sup-orders-email-${s.id}`}>
                      <MailQuestion size={11} />
                      <a href={`mailto:${s.orders_email}`} className="text-ink/70 hover:text-ink truncate max-w-[180px]" title={`Ordini: ${s.orders_email}`} onClick={(e) => e.stopPropagation()}>Ord. {s.orders_email}</a>
                    </span>
                  )}
                  {s.payment_terms && (
                    <span className="text-slate1">Pag. <span className="text-ink/80">{s.payment_terms}</span></span>
                  )}
                  {(() => {
                    const alert = getIncreaseAlert(s);
                    if (!alert) return null;
                    const styles = {
                      today:    "bg-rose-100 text-rose-800 border border-rose-300",
                      active:   "bg-rose-50 text-rose-700 border border-rose-200",
                      imminent: "bg-amber-100 text-amber-900 border border-amber-300 animate-pulse",
                      future:   "bg-amber-50 text-amber-800 border border-amber-200",
                      past:     "bg-slate-100 text-slate-600 border border-slate-200",
                      set:      "bg-amber-50 text-amber-800 border border-amber-200",
                    };
                    const what = alert.newListino ? "Nuovo listino" : `Aumento +${alert.pct}%`;
                    const labels = {
                      today:    `${what} in vigore da oggi`,
                      active:   `${what} in vigore da ${alert.days} ${alert.days === 1 ? "giorno" : "giorni"}`,
                      imminent: `${what} tra ${alert.days} ${alert.days === 1 ? "giorno" : "giorni"}`,
                      future:   `${what} dal ${alert.date ? alert.date.toLocaleDateString("it-IT") : ""}`,
                      past:     `${what} dal ${alert.date ? alert.date.toLocaleDateString("it-IT") : ""}`,
                      set:      what,
                    };
                    const Icon = (alert.state === "today" || alert.state === "imminent" || alert.state === "active") ? AlertTriangle : TrendingUp;
                    return (
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10.5px] tracking-[0.05em] ${styles[alert.state]}`}
                        data-testid={`sup-increase-${s.id}`}
                        data-state={alert.state}
                        title={alert.date ? `Decorrenza: ${alert.date.toLocaleDateString("it-IT")}` : "Aumento listino configurato"}
                      >
                        <Icon size={11} />
                        {labels[alert.state]}
                      </span>
                    );
                  })()}
                  {(() => {
                    const od = getOrderDeadlineAlert(s);
                    if (!od) return null;
                    const odStyles = {
                      today:    "bg-rose-100 text-rose-800 border border-rose-300 animate-pulse",
                      imminent: "bg-rose-100 text-rose-800 border border-rose-300 animate-pulse",
                      future:   "bg-blue-50 text-blue-800 border border-blue-200",
                      past:     "bg-slate-100 text-slate-600 border border-slate-200",
                    };
                    const odLabels = {
                      today:    `Termine ordine: oggi`,
                      imminent: `Termine ordine: ${od.days === 1 ? "domani" : `tra ${od.days} giorni`}`,
                      future:   `Termine ordine: ${od.date.toLocaleDateString("it-IT")}`,
                      past:     `Termine ordine scaduto (${od.date.toLocaleDateString("it-IT")})`,
                    };
                    const OdIcon = (od.state === "today" || od.state === "imminent") ? AlertTriangle : TrendingUp;
                    return (
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10.5px] tracking-[0.05em] ${odStyles[od.state]}`}
                        data-testid={`sup-order-deadline-${s.id}`}
                        data-state={od.state}
                        title={`Termine ordine per consegne: ${od.date.toLocaleDateString("it-IT")}`}
                      >
                        <OdIcon size={11} />
                        {odLabels[od.state]}
                      </span>
                    );
                  })()}
                </div>

                <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] tracking-[0.1em] uppercase text-slate1">
                  {s.updated_at && (
                    <span
                      className={isAssetsStale(s) ? "text-rose-600 font-semibold animate-pulse" : ""}
                      data-testid={`sup-updated-${s.id}`}
                      title={isAssetsStale(s) ? "Listini caricati da più di 2 mesi — verifica che siano ancora validi" : undefined}
                    >
                      Dati agg. {new Date(s.updated_at).toLocaleDateString("it-IT")}
                    </span>
                  )}
                  {s.last_asset_at && (
                    <span
                      className={isAssetsStale(s) ? "text-rose-600 font-semibold animate-pulse" : ""}
                      title={isAssetsStale(s) ? "Ultimo file caricato oltre 2 mesi fa" : undefined}
                    >
                      Ultimo file {new Date(s.last_asset_at).toLocaleDateString("it-IT")}
                    </span>
                  )}
                  {s.listino_reminder_snooze_until && new Date(s.listino_reminder_snooze_until).getTime() > Date.now() && (
                    <span className="text-sky-700" title={`Proroga registrata${s.listino_reminder_count ? ` (${s.listino_reminder_count} in totale)` : ""} — l'avviso "scaduto" riapparirà a questa data`} data-testid={`sup-reminder-date-${s.id}`}>
                      Prossimo controllo {new Date(s.listino_reminder_snooze_until).toLocaleDateString("it-IT")}
                    </span>
                  )}
                  {(s.listino_count || 0) > 0 && !s.agent_email && (
                    <span className="text-amber-600 font-semibold" title="Email agente mancante: la bozza sollecito verrà generata senza destinatario. Compila l'email agente nella scheda fornitore." data-testid={`sup-no-agent-email-${s.id}`}>
                      Manca email agente
                    </span>
                  )}
                </div>
              </div>
              {(s.listino_count || 0) > 0 && (
                <button onClick={(e) => { e.stopPropagation(); openListinoDraft(s); }} disabled={draftLoading === s.id} className="text-slate1 hover:text-ink self-center disabled:opacity-40" data-testid={`sup-reminder-${s.id}`} title="Genera la bozza del sollecito «listino ancora aggiornato?» da usare nel tuo client di posta">
                  {draftLoading === s.id ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}
                </button>
              )}
              <button onClick={() => setEditing(s)} className="text-slate1 hover:text-ink self-center" data-testid={`sup-edit-${s.id}`} title="Modifica"><Pencil size={16} /></button>
              <button onClick={() => delSupplier(s.id, s.name)} className="text-slate1 hover:text-destructive self-center" data-testid={`sup-del-${s.id}`} title="Cancella"><Trash2 size={16} /></button>
            </div>
              );
            })}
          {supVisible < filteredSuppliers.length && (
            <div className="p-4 flex justify-center" data-testid="sup-loading-more">
              <span className="inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-slate1">
                <Loader2 size={13} className="animate-spin" /> Caricamento altri fornitori… ({filteredSuppliers.length - supVisible})
              </span>
            </div>
          )}
        </div>
      </div>

      {draft && (
        <div className="fixed inset-0 z-50 bg-ink/40 flex items-center justify-center p-6" onClick={() => setDraft(null)}>
          <div className="bg-cream max-w-2xl w-full p-8 rounded-sm max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="listino-draft-modal">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase text-slate1">Bozza sollecito listino</div>
                <div className="font-display text-xl text-ink">{draft.supplier_name}</div>
              </div>
              <button onClick={() => setDraft(null)} data-testid="listino-draft-close"><X size={18} className="text-slate1" /></button>
            </div>

            <p className="mt-3 text-[12px] text-slate1 leading-relaxed">
              Nessun invio automatico: usa questa bozza nel tuo client di posta. Le copertine dei listini vanno scaricate (PDF) e allegate a mano.
            </p>

            {(draft.listini || []).length > 0 && (
              <div className="mt-4 border border-ink/10 rounded-sm bg-white/60 p-3" data-testid="listino-draft-list">
                <div className="text-[10px] tracking-[0.2em] uppercase text-slate1 mb-1.5">
                  Listini in cartella ({draft.count})
                </div>
                <ul className="space-y-0.5 max-h-32 overflow-y-auto">
                  {draft.listini.map((l, i) => (
                    <li key={i} className="text-[12px] text-ink flex items-start gap-1.5" data-testid={`listino-draft-name-${i}`}>
                      <FileText size={12} className="text-slate1 shrink-0 mt-0.5" />
                      <span className="flex-1"><span className="font-medium">{l.title}</span>{l.uploaded_at && <span className="text-slate1"> · caricato il {new Date(l.uploaded_at).toLocaleDateString("it-IT")}</span>}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-5 space-y-3">
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-slate1 block mb-1">Destinatario (agente)</label>
                {draft.to ? (
                  <input readOnly value={draft.to} className="w-full border border-ink/20 px-3 py-2 rounded-sm bg-white text-ink" data-testid="listino-draft-to" />
                ) : (
                  <div className="w-full border border-amber-300 bg-amber-50 px-3 py-2 rounded-sm text-amber-700 text-[12px]" data-testid="listino-draft-no-to">
                    Email agente mancante nella scheda fornitore — compilala per avere il destinatario precompilato.
                  </div>
                )}
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-slate1 block mb-1">Oggetto</label>
                <input value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} className="w-full border border-ink/20 px-3 py-2 rounded-sm bg-white text-ink" data-testid="listino-draft-subject" />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.2em] uppercase text-slate1 block mb-1">Testo</label>
                <textarea value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })} rows={11} className="w-full border border-ink/20 px-3 py-2 rounded-sm bg-white text-ink text-[13px] leading-relaxed font-mono" data-testid="listino-draft-body" />
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <button onClick={openInMailClient} className="inline-flex items-center gap-1.5 px-3 py-2 bg-ink text-cream rounded-sm text-[11px] tracking-[0.15em] uppercase hover:opacity-90 transition-opacity" data-testid="listino-draft-mailto">
                <ExternalLink size={13} /> Apri nel client mail
              </button>
              <button onClick={copyDraftText} className="inline-flex items-center gap-1.5 px-3 py-2 border border-ink/20 text-ink rounded-sm text-[11px] tracking-[0.15em] uppercase hover:bg-cream2 transition-colors" data-testid="listino-draft-copy">
                <Copy size={13} /> Copia testo
              </button>
              {draft.covers_url && (
                <button onClick={downloadDraftCovers} disabled={draftImgBusy} className="inline-flex items-center gap-1.5 px-3 py-2 border border-ink/20 text-ink rounded-sm text-[11px] tracking-[0.15em] uppercase hover:bg-cream2 transition-colors disabled:opacity-40" data-testid="listino-draft-covers">
                  {draftImgBusy ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} Scarica copertine (PDF{draft.covers_count ? ` · ${draft.covers_count}` : ""})
                </button>
              )}
            </div>
            {draft.covers_limited && (
              <p className="mt-1.5 text-[11px] text-amber-700" data-testid="listino-draft-covers-note">
                Il PDF contiene le copertine dei primi 10 listini; nel testo sono elencati tutti i nomi.
              </p>
            )}

            <div className="mt-6 pt-5 border-t border-ink/10">
              <div className="text-[10px] tracking-[0.2em] uppercase text-gold inline-flex items-center gap-1.5 mb-2">
                <CalendarClock size={11} /> Proroga · prossimo controllo
              </div>
              <p className="text-[12px] text-slate1 leading-relaxed mb-3">
                Registra la proroga per sospendere l'avviso «listino scaduto». Lascia la data vuota per rimandare di <strong>2 mesi</strong> da oggi, oppure scegli una data.
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <input type="date" value={prorogaDate} onChange={(e) => setProrogaDate(e.target.value)} className="border border-ink/20 px-3 py-2 rounded-sm bg-white text-ink" data-testid="listino-proroga-date" />
                <button onClick={registerProroga} disabled={prorogaBusy} className="inline-flex items-center gap-1.5 px-3 py-2 bg-gold text-ink rounded-sm text-[11px] tracking-[0.15em] uppercase hover:opacity-90 transition-opacity disabled:opacity-40" data-testid="listino-proroga-save">
                  {prorogaBusy ? <Loader2 size={13} className="animate-spin" /> : <CalendarClock size={13} />} Registra proroga
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {editing && (
        <div className="fixed inset-0 z-50 bg-ink/40 flex items-center justify-center p-6" onClick={() => setEditing(null)}>
          <div className="bg-cream max-w-2xl w-full p-8 rounded-sm max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="sup-edit-modal">
            <div className="flex items-center justify-between"><div className="font-display text-xl text-ink">Modifica fornitore</div><button onClick={() => setEditing(null)}><X size={18} className="text-slate1" /></button></div>
            <div className="mt-5 space-y-3">
              <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Nome" className="w-full border border-ink/20 px-3 py-2 rounded-sm bg-white" />
              <input value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} placeholder="Descrizione" className="w-full border border-ink/20 px-3 py-2 rounded-sm bg-white" />
              <div className="grid grid-cols-2 gap-3 items-start">
                <input type="number" value={editing.lead_time_days} onChange={(e) => setEditing({ ...editing, lead_time_days: e.target.value })} placeholder="Lead time gg" className="border border-ink/20 px-3 py-2 rounded-sm bg-white" />
                <div>
                  <label className="text-[10px] tracking-[0.2em] uppercase text-slate1 block mb-1">Sconto riservato</label>
                  <DiscountChainInput
                    value={editing.discount_chain && editing.discount_chain.length ? editing.discount_chain : (editing.discount_pct ? [editing.discount_pct, 0, 0] : [])}
                    onChange={(arr) => setEditing({ ...editing, discount_chain: arr })}
                    testidPrefix="sup-edit-discount-chain"
                  />
                </div>
              </div>
              <input value={editing.website || ""} onChange={(e) => setEditing({ ...editing, website: e.target.value })} placeholder="https://www.fornitore.com" className="w-full border border-ink/20 px-3 py-2 rounded-sm bg-white" />

              <div className="pt-4 border-t border-ink/10">
                <div className="text-[10px] tracking-[0.2em] uppercase text-gold inline-flex items-center gap-1.5 mb-2">
                  <Lock size={10} /> Solo admin · informazioni commerciali
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="text-[10px] tracking-[0.2em] uppercase text-slate1 block mb-1">Sconto acquisto</label>
                  <DiscountChainInput
                    value={editing.purchase_discount_chain && editing.purchase_discount_chain.length ? editing.purchase_discount_chain : (editing.purchase_discount_pct ? [editing.purchase_discount_pct, 0, 0] : [])}
                    onChange={(arr) => setEditing({ ...editing, purchase_discount_chain: arr })}
                    testidPrefix="sup-edit-purchase-chain"
                  />
                </div>
                <input value={editing.payment_terms || ""} onChange={(e) => setEditing({ ...editing, payment_terms: e.target.value })} placeholder="Pagamento (es. 30/60 gg fm)" className="border border-ink/20 px-3 py-2 rounded-sm bg-white" />
                <input value={editing.agent_name || ""} onChange={(e) => setEditing({ ...editing, agent_name: e.target.value })} placeholder="Agente" className="border border-ink/20 px-3 py-2 rounded-sm bg-white" />
                <input value={editing.agent_phone || ""} onChange={(e) => setEditing({ ...editing, agent_phone: e.target.value })} placeholder="Telefono agente" className="border border-ink/20 px-3 py-2 rounded-sm bg-white" />
                <input type="number" min={0} step="100" value={editing.year_end_bonus_budget ?? 0} onChange={(e) => setEditing({ ...editing, year_end_bonus_budget: e.target.value })} placeholder="Budget premio fine anno €" className="col-span-2 border border-ink/20 px-3 py-2 rounded-sm bg-white" />
                <div className="col-span-2 grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] tracking-[0.2em] uppercase text-slate1 block mb-1">Aumento listino (%)</label>
                    <input type="number" min={0} step="0.5" value={editing.price_increase_pct ?? 0} onChange={(e) => setEditing({ ...editing, price_increase_pct: e.target.value })} placeholder="es. 5" disabled={Boolean(editing.price_increase_new_listino)} className={`w-full border border-ink/20 px-3 py-2 rounded-sm ${editing.price_increase_new_listino ? "bg-cream/50 text-slate-400 cursor-not-allowed" : "bg-white"}`} data-testid="sup-edit-increase-pct" />
                    <label className="mt-1.5 flex items-center gap-1.5 text-[11px] text-slate1 cursor-pointer select-none">
                      <input type="checkbox" checked={Boolean(editing.price_increase_new_listino)} onChange={(e) => setEditing({ ...editing, price_increase_new_listino: e.target.checked, ...(e.target.checked ? { price_increase_pct: 0 } : {}) })} data-testid="sup-edit-increase-newlistino" />
                      Nuovo listino (senza %)
                    </label>
                  </div>
                  <div>
                    <label className="text-[10px] tracking-[0.2em] uppercase text-slate1 block mb-1">Decorrenza aumento</label>
                    <input type="date" value={editing.price_increase_date || ""} onChange={(e) => setEditing({ ...editing, price_increase_date: e.target.value })} className="w-full border border-ink/20 px-3 py-2 rounded-sm bg-white" data-testid="sup-edit-increase-date" />
                  </div>
                </div>
              </div>
              <textarea value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} placeholder="Note interne" rows={3} className="w-full border border-ink/20 px-3 py-2 rounded-sm bg-white" />
            </div>
            <button onClick={saveEditing} className="mt-5 bg-ink text-cream px-5 py-2.5 text-[11px] tracking-[0.2em] uppercase rounded-sm" data-testid="sup-edit-save">Salva</button>
          </div>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-ink/40" onClick={() => { setOpen(null); setIntegrityBrokenIds(new Set()); setIntegritySummary(null); }}>
          <div className="absolute right-0 top-0 bottom-0 w-full max-w-6xl bg-cream overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="sup-detail">
            <div className="p-6 border-b border-ink/10 flex items-center justify-between sticky top-0 bg-cream z-10 gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="text-[10px] tracking-[0.2em] uppercase text-slate1">Asset fornitore</div>
                <div className="font-display text-xl text-ink truncate">{open.name}</div>
              </div>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                <button
                  onClick={() => verifySupplierIntegrity(open.id)}
                  disabled={integrityBusy}
                  className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.2em] uppercase border border-ink/30 text-ink hover:bg-ink hover:text-cream px-3 py-1.5 rounded-sm transition-colors disabled:opacity-50"
                  data-testid="sup-verify-integrity"
                  title="Verifica che tutti i file del fornitore siano leggibili su storage"
                >
                  {integrityBusy ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
                  {integrityBusy ? "Verifica…" : "Verifica integrità"}
                </button>
                <button
                  onClick={() => forceRedownloadSupplier(open.id)}
                  className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.2em] uppercase border border-gold/40 text-gold hover:bg-gold hover:text-cream px-3 py-1.5 rounded-sm transition-colors"
                  data-testid="sup-force-redownload"
                  title="Forza il re-download dei file di questo fornitore (usa se preview fallisce nonostante verifica integrità OK)"
                >
                  <RefreshCw size={11} /> Forza re-download
                </button>
                {integritySummary && (
                  <span
                    className={`text-[10px] tracking-[0.18em] uppercase px-2 py-1 rounded-sm border ${
                      integritySummary.broken === 0
                        ? "bg-emerald-50 text-emerald-700 border-emerald-300"
                        : "bg-rose-50 text-rose-700 border-rose-300"
                    }`}
                    data-testid="sup-integrity-summary"
                  >
                    {integritySummary.broken === 0
                      ? `✓ ${integritySummary.ok}/${integritySummary.checked} OK`
                      : `✗ ${integritySummary.broken}/${integritySummary.checked} rotti`}
                  </span>
                )}
                <button onClick={() => { setOpen(null); setIntegrityBrokenIds(new Set()); setIntegritySummary(null); }}><X size={18} className="text-slate1" /></button>
              </div>
            </div>
            <div className="p-6 lg:grid lg:grid-cols-2 lg:gap-6 space-y-6 lg:space-y-0">
              {/* LEFT COLUMN: Upload + Files */}
              <div className="space-y-6">
              <div
                className={`bg-white border ${assetUpload.dragOver ? "border-ink border-2 bg-cream2" : "border-ink/10"} p-4 rounded-sm transition-colors`}
                {...assetUpload.handlers}
                data-testid="sup-upload-dropzone"
              >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                  <select value={kind} onChange={(e) => setKind(e.target.value)} className="border border-ink/20 px-3 py-2 rounded-sm bg-white" data-testid="sup-upload-kind">
                    {ASSET_KINDS.map((k) => <option key={k.value} value={k.value}>{k.adminOnly ? `${k.label} · 🔒 Solo admin` : k.label}</option>)}
                  </select>
                  <label className={`md:col-span-2 cursor-pointer border-2 border-dashed ${assetUpload.dragOver ? "border-ink bg-cream2" : "border-ink/20"} p-3 rounded-sm text-center hover:bg-cream2 transition-colors`}>
                    <Upload size={16} className="inline mr-2 text-slate1" />
                    <span className="text-[11px] tracking-[0.2em] uppercase text-slate1">
                      {assetUpload.uploading
                        ? (assetUpload.progress ? `Caricamento ${assetUpload.progress.done}/${assetUpload.progress.total}…` : "Caricamento...")
                        : (assetUpload.dragOver ? "Rilascia qui i file" : "Trascina qui o clicca per caricare (più file, fino a 500 MB — chunked auto per file >5 MB)")}
                    </span>
                    <input type="file" className="hidden" {...assetUpload.inputProps} data-testid="sup-upload" />
                  </label>
                </div>
                <UploadProgressBar progress={assetUpload.progress} />
                <UploadDetailedProgress perFile={assetUpload.perFile} />
                <button onClick={() => setExtOpen(!extOpen)} className="text-[11px] tracking-[0.2em] uppercase text-slate1 hover:text-ink inline-flex items-center gap-1" data-testid="sup-add-link">
                  <Link2 size={12} /> {extOpen ? "Nascondi" : "...oppure aggiungi link esterno (Drive/Dropbox)"}
                </button>
                {extOpen && (
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                    <select value={extForm.kind} onChange={(e) => setExtForm({ ...extForm, kind: e.target.value })} className="border border-ink/20 px-3 py-2 rounded-sm bg-white">
                      {ASSET_KINDS.map((k) => <option key={k.value} value={k.value}>{k.adminOnly ? `${k.label} · 🔒 Solo admin` : k.label}</option>)}
                    </select>
                    <input value={extForm.title} onChange={(e) => setExtForm({ ...extForm, title: e.target.value })} placeholder="Titolo" className="border border-ink/20 px-3 py-2 rounded-sm bg-white" />
                    <input value={extForm.external_url} onChange={(e) => setExtForm({ ...extForm, external_url: e.target.value })} placeholder="https://..." className="border border-ink/20 px-3 py-2 rounded-sm bg-white" />
                    <button onClick={() => addExternal(open.id)} className="md:col-span-3 bg-ink text-cream px-4 py-2 text-[11px] tracking-[0.2em] uppercase rounded-sm">Aggiungi link</button>
                  </div>
                )}
                <button onClick={() => setGdriveOpen(!gdriveOpen)} className="text-[11px] tracking-[0.2em] uppercase text-slate1 hover:text-ink inline-flex items-center gap-1 mt-2" data-testid="sup-add-gdrive">
                  <Link2 size={12} /> {gdriveOpen ? "Nascondi" : "...oppure indicizza una cartella Google Drive (file 3D/CAD)"}
                </button>
                {gdriveOpen && (
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3 bg-cream2/40 border border-gold/30 p-3 rounded-sm">
                    <select value={gdriveForm.kind} onChange={(e) => setGdriveForm({ ...gdriveForm, kind: e.target.value })} className="border border-ink/20 px-3 py-2 rounded-sm bg-white">
                      {ASSET_KINDS.map((k) => <option key={k.value} value={k.value}>{k.adminOnly ? `${k.label} · 🔒 Solo admin` : k.label}</option>)}
                    </select>
                    <input value={gdriveForm.title} onChange={(e) => setGdriveForm({ ...gdriveForm, title: e.target.value })} placeholder="Titolo (opzionale: usa il nome cartella)" className="border border-ink/20 px-3 py-2 rounded-sm bg-white" data-testid="gdrive-title" />
                    <input value={gdriveForm.folder_url} onChange={(e) => setGdriveForm({ ...gdriveForm, folder_url: e.target.value })} placeholder="https://drive.google.com/drive/folders/…" className="border border-ink/20 px-3 py-2 rounded-sm bg-white" data-testid="gdrive-url" />
                    <div className="md:col-span-3 text-[10px] text-slate1 italic leading-relaxed">
                      <strong>Solo Google Drive</strong> (no OneDrive, no Dropbox). Imposta la cartella Drive come &ldquo;Chiunque con il link può visualizzare&rdquo;. Il sistema leggerà i nomi dei file ed eseguirà la Magic Search anche al loro interno.
                    </div>
                    <button onClick={() => addGdriveFolder(open.id)} disabled={gdriveBusy} className="md:col-span-3 bg-ink text-cream px-4 py-2 text-[11px] tracking-[0.2em] uppercase rounded-sm disabled:opacity-50" data-testid="gdrive-submit">
                      {gdriveBusy ? "Indicizzazione…" : "Indicizza cartella Drive"}
                    </button>
                  </div>
                )}
              </div>

              <div>
                <MagicSearch
                  supplierId={open.id}
                  assets={assets}
                  onOpenAtPage={handleMagicOpenAtPage}
                  onOpenFile={handleMagicOpenFile}
                />
                {(() => {
                  // Mostra un bottone "Indicizza tutti i PDF" se ci sono PDF non indicizzati
                  const pdfsNotIndexed = assets.filter((a) => {
                    const isPdf = (a.content_type || "").toLowerCase() === "application/pdf" || (a.filename || "").toLowerCase().endsWith(".pdf");
                    return isPdf && !a.archived && a.indexed_status !== "indexed" && a.indexed_status !== "indexing";
                  });
                  if (pdfsNotIndexed.length === 0) return null;
                  return (
                    <div className="mb-4 flex items-center justify-between gap-3 bg-cream border border-gold/40 rounded-sm px-4 py-3">
                      <div className="text-[12px] text-ink/85">
                        <Sparkles size={12} className="inline mr-1 text-gold" />
                        <b>{pdfsNotIndexed.length}</b> PDF non ancora indicizzati per Magic Search.
                      </div>
                      <button
                        onClick={async () => {
                          if (!window.confirm(`Avviare l'indicizzazione di ${pdfsNotIndexed.length} PDF di ${open.name}? Procede 1 alla volta in background, puoi continuare a usare l'app.`)) return;
                          try {
                            const r = await api.post(`/admin/suppliers/${open.id}/assets/reindex`);
                            toast.success(`Indicizzazione avviata su ${r.data.scheduled} PDF`);
                            // Local update + refresh
                            setAssets((prev) => prev.map((a) => {
                              const isPdf = (a.content_type || "").toLowerCase() === "application/pdf" || (a.filename || "").toLowerCase().endsWith(".pdf");
                              if (isPdf && a.indexed_status !== "indexed") return { ...a, indexed_status: "indexing", index_error: null };
                              return a;
                            }));
                            setTimeout(() => loadAssets(open.id), 5000);
                          } catch (e) {
                            toast.error(e.response?.data?.detail || "Errore avvio indicizzazione");
                          }
                        }}
                        className="px-4 py-2 bg-gold text-cream text-[11px] tracking-[0.2em] uppercase rounded-sm hover:bg-gold/90 transition-colors inline-flex items-center gap-1"
                        data-testid="sup-reindex-all"
                      >
                        <Sparkles size={12} /> Indicizza tutti i PDF
                      </button>
                    </div>
                  );
                })()}
                <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
                  <div className="flex items-center gap-2.5 flex-wrap">
                    <div className="text-[10px] tracking-[0.2em] uppercase text-slate1">
                      File (
                      {assetQuery
                        ? <><span data-testid="adm-asset-filtered-count">{filteredAssetsCount}</span> / {assets.length}</>
                        : assets.length}
                      )
                    </div>
                    {(() => {
                      const staleIds = assets.filter((x) => isStale(x)).map((x) => x.id);
                      if (!staleIds.length) return null;
                      return (
                        <button
                          type="button"
                          disabled={bulkBusy}
                          onClick={() => renewAssets(staleIds, `Prorogare tutti i ${staleIds.length} file scaduti di ${open.name}? La data di caricamento diventa oggi, come se fossero stati ricaricati.`)}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] tracking-[0.1em] uppercase border border-emerald-400 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors disabled:opacity-50"
                          title="Aggiorna a oggi la data di caricamento di tutti i file scaduti (più di 2 mesi)"
                          data-testid="adm-asset-renew-all"
                        >
                          <RotateCw size={10} /> Proroga tutti ({staleIds.length} scadut{staleIds.length === 1 ? "o" : "i"})
                        </button>
                      );
                    })()}
                  </div>
                  {assets.length > 10 && (
                    <div className="relative flex items-center" data-testid="adm-asset-search-wrap">
                      <Search size={12} className="absolute left-2 text-slate1" />
                      <input
                        type="text"
                        value={assetQuery}
                        onChange={(e) => setAssetQuery(e.target.value)}
                        placeholder="Cerca tra gli asset (nome file)…"
                        className="pl-7 pr-8 py-1.5 text-xs bg-white border border-ink/15 rounded-sm w-72 focus:outline-none focus:border-ink"
                        data-testid="adm-asset-search-input"
                      />
                      {assetQuery && (
                        <button
                          onClick={() => setAssetQuery("")}
                          className="absolute right-1 p-1 text-slate1 hover:text-ink"
                          title="Pulisci"
                          data-testid="adm-asset-search-clear"
                        >
                          <X size={11} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
                {/* Barra di azione massiva — compare solo quando ci sono asset selezionati */}
                {selectedAssetIds.size > 0 && (
                  <div
                    className="mb-3 sticky top-2 z-20 bg-ink text-cream rounded-sm shadow-lg px-3 py-2 flex items-center gap-3 flex-wrap"
                    data-testid="adm-asset-bulk-bar"
                  >
                    <span className="text-xs tracking-[0.15em] uppercase" data-testid="adm-asset-bulk-count">
                      {selectedAssetIds.size} selezionati
                    </span>
                    <span className="text-[10px] tracking-[0.15em] uppercase text-cream/60">Sposta in:</span>
                    <select
                      value={bulkTargetKind}
                      onChange={(e) => setBulkTargetKind(e.target.value)}
                      className="bg-cream text-ink border border-ink/20 px-2 py-1 rounded-sm text-[11px] focus:outline-none"
                      data-testid="adm-asset-bulk-target"
                    >
                      {ASSET_KINDS.map((k) => (
                        <option key={k.value} value={k.value}>{k.label}</option>
                      ))}
                    </select>
                    <button
                      onClick={runBulkMove}
                      disabled={bulkBusy}
                      className="bg-gold text-ink px-3 py-1 text-[11px] tracking-[0.2em] uppercase rounded-sm hover:opacity-90 disabled:opacity-50 transition-opacity"
                      data-testid="adm-asset-bulk-apply"
                    >
                      {bulkBusy ? "Spostamento…" : "Sposta"}
                    </button>
                    <button
                      onClick={() => renewAssets(Array.from(selectedAssetIds), `Prorogare ${selectedAssetIds.size} file? La data di caricamento diventa oggi, come se fossero stati ricaricati.`)}
                      disabled={bulkBusy}
                      className="inline-flex items-center gap-1.5 bg-emerald-500 text-ink px-3 py-1 text-[11px] tracking-[0.2em] uppercase rounded-sm hover:opacity-90 disabled:opacity-50 transition-opacity"
                      title="La data di caricamento dei file selezionati diventa oggi"
                      data-testid="adm-asset-bulk-renew"
                    >
                      <RotateCw size={11} /> Proroga
                    </button>
                    <button
                      onClick={() => setShareModalOpen(true)}
                      disabled={bulkBusy}
                      className="inline-flex items-center gap-1.5 bg-cream text-ink px-3 py-1 text-[11px] tracking-[0.2em] uppercase rounded-sm hover:opacity-90 disabled:opacity-50 transition-opacity"
                      title="Crea un link temporaneo (48h) per condividere i file selezionati con un cliente esterno"
                      data-testid="adm-asset-bulk-share"
                    >
                      <Link2 size={11} /> Link 48h
                    </button>
                    <button
                      onClick={() => setSelectedAssetIds(new Set())}
                      className="text-cream/70 hover:text-cream text-[10px] tracking-[0.15em] uppercase ml-auto"
                      data-testid="adm-asset-bulk-clear"
                    >
                      Pulisci
                    </button>
                  </div>
                )}
                <AssetShareLinkModal
                  open={shareModalOpen}
                  onClose={() => setShareModalOpen(false)}
                  selectedAssets={assets.filter((a) => selectedAssetIds.has(a.id))}
                  supplierName={open?.name || ""}
                />
                {assets.length === 0 ? (
                  <div className="bg-white border border-ink/10 rounded-sm p-4 text-sm text-slate1">Nessun asset.</div>
                ) : filteredAssetsCount === 0 ? (
                  <div className="bg-white border border-ink/10 rounded-sm p-4 text-sm text-slate1" data-testid="adm-asset-search-empty">
                    Nessun asset corrisponde a &laquo;{assetQuery}&raquo;.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {groupedAssets.map((group) => {
                      const allInGroupSelected = group.items.length > 0 && group.items.every((a) => selectedAssetIds.has(a.id));
                      const someInGroupSelected = group.items.some((a) => selectedAssetIds.has(a.id));
                      return (
                      <div key={group.value} data-testid={`adm-asset-group-${group.value}`} className="bg-white border border-ink/10 rounded-sm">
                        <div className="px-3 py-2 border-b border-ink/10 flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={allInGroupSelected}
                              ref={(el) => { if (el) el.indeterminate = !allInGroupSelected && someInGroupSelected; }}
                              onChange={() => toggleSelectGroup(group.items, allInGroupSelected)}
                              className="accent-gold cursor-pointer"
                              title={allInGroupSelected ? "Deseleziona tutti in questo gruppo" : "Seleziona tutti in questo gruppo"}
                              data-testid={`adm-asset-group-select-${group.value}`}
                            />
                            <Folder size={12} className="text-slate1" />
                            <div className="text-[10px] tracking-[0.2em] uppercase text-ink">{group.label}</div>
                            {group.adminOnly && (
                              <span className="inline-flex items-center gap-1 text-[9px] tracking-[0.2em] uppercase text-gold bg-gold/10 px-1.5 py-0.5 rounded-sm">
                                <Lock size={9} /> Solo admin
                              </span>
                            )}
                          </div>
                          <div className="text-[10px] tracking-[0.15em] uppercase text-slate1">{group.items.length} file</div>
                        </div>
                        <div className="divide-y divide-ink/10">
                          {group.items.map((a) => {
                            const isPdf = (a.content_type || "").toLowerCase() === "application/pdf" || (a.filename || "").toLowerCase().endsWith(".pdf");
                            const idx = a.indexed_status;
                            const liveProg = indexProgress[a.id];
                            // Label dinamica quando indexing con progress
                            let indexingLabel = "Indicizzazione…";
                            if (idx === "indexing" && liveProg) {
                              if (liveProg.phase === "downloading") indexingLabel = "Scaricamento…";
                              else if (liveProg.phase === "extracting" && liveProg.pages_total > 0) {
                                indexingLabel = `Estrazione · ${liveProg.pages_done} / ${liveProg.pages_total} pagine`;
                              } else if (liveProg.phase === "saving") {
                                indexingLabel = "Salvataggio…";
                              }
                            }
                            const indexBadge = isPdf ? (
                              a.archived ? { cls: "bg-slate-100 text-slate-500 border-slate-300", label: "Archiviato · fuori indice e ricerche", icon: Archive } :
                              idx === "indexed" ? { cls: "bg-emerald-50 text-emerald-700 border-emerald-300", label: `Indicizzato${a.indexed_pages ? ` · ${a.indexed_pages} pag.` : ""}`, icon: Sparkles } :
                              idx === "indexing" ? { cls: "bg-amber-50 text-amber-800 border-amber-300", label: indexingLabel, icon: Loader2 } :
                              idx === "skipped" ? { cls: "bg-amber-50 text-amber-800 border-amber-300", label: "Saltato (>100MB)", icon: AlertCircle } :
                              idx === "paused" ? { cls: "bg-slate-50 text-slate-600 border-slate-300", label: "In pausa", icon: AlertCircle } :
                              idx === "error" ? { cls: "bg-rose-50 text-rose-700 border-rose-300", label: "Errore indicizzazione", icon: AlertCircle } :
                              { cls: "bg-slate-50 text-slate-600 border-slate-300", label: "Non indicizzato", icon: AlertCircle }
                            ) : null;
                            // "Indicizza ora" disponibile per qualsiasi PDF che non sia indicizzato o in corso
                            // (include: undefined/null = mai indicizzato, skipped, paused, error)
                            const canForceReindex = isPdf && !a.archived && idx !== "indexed" && idx !== "indexing";
                            // Indicizzazione "bloccata": status=indexing ma nessun task attivo (heartbeat assente)
                            const isStuckIndexing = isPdf && idx === "indexing" && progressPolled && !liveProg;
                            const isStorageBroken = integrityBrokenIds.has(a.id);
                            return (
                            <div key={a.id} className={`p-3 ${isStorageBroken ? "bg-rose-50 border-l-4 border-rose-500" : ""}`} data-testid={`asset-${a.id}`}>
                              <div className="flex items-start gap-3">
                              <input
                                type="checkbox"
                                checked={selectedAssetIds.has(a.id)}
                                onChange={() => toggleAssetSelection(a.id)}
                                className="accent-gold cursor-pointer shrink-0 mt-1"
                                title="Seleziona per operazioni in blocco"
                                data-testid={`asset-select-${a.id}`}
                              />
                              {a.content_type === "external/url" ? (
                                <Link2 size={14} className="text-gold shrink-0" />
                              ) : a.content_type === "gdrive/folder" ? (
                                <Folder size={14} className="text-gold shrink-0" />
                              ) : isPdf && isPreviewable(a) ? (
                                <AssetCoverThumbnail
                                  assetId={a.id}
                                  testid={`asset-cover-${a.id}`}
                                  title={`Apri anteprima · ${a.filename || a.title || ""}`}
                                  onClick={() => previewAsset(a)}
                                />
                              ) : (
                                <FileText size={14} className="text-slate1 shrink-0" />
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="text-sm text-ink font-medium break-words leading-snug" data-testid={`asset-title-${a.id}`}>{a.title}</div>
                                <div className="text-xs text-slate1">
                                  {a.size > 0 && <>{(a.size / 1024 / 1024).toFixed(2)} MB</>}
                                  {a.content_type === "external/url" && <> · link esterno</>}
                                  {a.content_type === "gdrive/folder" && (
                                    <>cartella Drive · {a.gdrive_files_count || 0} file</>
                                  )}
                                </div>
                                {isStorageBroken && (
                                  <div className="mt-1 inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase border border-rose-400 bg-rose-100 text-rose-800 px-2 py-0.5 rounded-sm font-medium" data-testid={`asset-broken-badge-${a.id}`}>
                                    <AlertCircle size={10} /> File rotto su storage · usa 🔁 Sostituisci
                                  </div>
                                )}
                                {a.created_at && (
                                  <div className={`text-[11px] mt-0.5 inline-flex items-center gap-1 ${isStale(a) ? "text-destructive font-medium" : "text-slate1/80"}`} data-testid={`asset-date-${a.id}`}>
                                    {isStale(a) && <AlertCircle size={11} />}
                                    Caricato il {new Date(a.created_at).toLocaleDateString("it-IT")}
                                    {isStale(a) && <span className="ml-1 uppercase tracking-[0.15em]">· da aggiornare</span>}
                                    {isStale(a) && (
                                      <button
                                        type="button"
                                        disabled={bulkBusy}
                                        onClick={() => renewAssets([a.id], `Prorogare "${a.title || a.filename}"? La data di caricamento diventa oggi, come se fosse stato ricaricato.`)}
                                        className="ml-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[9px] tracking-[0.12em] uppercase border border-emerald-400 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors disabled:opacity-50"
                                        title="Aggiorna la data di caricamento a oggi"
                                        data-testid={`asset-renew-${a.id}`}
                                      >
                                        <RotateCw size={9} /> Proroga
                                      </button>
                                    )}
                                  </div>
                                )}
                                {indexBadge && (
                                  <div className={`mt-1 inline-flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase border px-1.5 py-0.5 rounded-sm ${indexBadge.cls}`} data-testid={`asset-index-${a.id}`} title={a.index_error || ""}>
                                    <indexBadge.icon size={10} className={idx === "indexing" ? "animate-spin" : ""} />
                                    {indexBadge.label}
                                  </div>
                                )}
                                {a.kind === "listino" && !a.archived && (Number(open.price_increase_pct || 0) > 0 || open.price_increase_new_listino) && (open.price_increase_date || "") && (
                                  <label
                                    className="mt-1.5 ml-1 inline-flex items-center gap-1.5 text-[10px] tracking-[0.1em] uppercase text-slate1 cursor-pointer select-none"
                                    title="Se attivo, il badge +X% non verrà mostrato agli utenti su questo file"
                                    data-testid={`asset-exclude-price-increase-label-${a.id}`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={Boolean(a.exclude_price_increase)}
                                      onChange={(e) => toggleExcludePriceIncrease(open.id, a.id, e.target.checked)}
                                      className="accent-gold cursor-pointer"
                                      data-testid={`asset-exclude-price-increase-${a.id}`}
                                    />
                                    <span>Nascondi aumento listino su questo file</span>
                                  </label>
                                )}
                              </div>
                              </div>
                              <div className="mt-2 flex items-center gap-2 flex-wrap sm:justify-end" data-testid={`asset-actions-${a.id}`}>
                              {canForceReindex && (
                                <button
                                  onClick={() => forceReindexAsset(open.id, a.id)}
                                  className="inline-flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase border border-gold/40 text-gold hover:bg-gold hover:text-cream px-2 py-1 rounded-sm transition-colors"
                                  data-testid={`asset-reindex-${a.id}`}
                                  title="Forza indicizzazione (bypassa limite 100 MB)"
                                >
                                  <Sparkles size={11} /> Indicizza ora
                                </button>
                              )}
                              {isStuckIndexing && (
                                <button
                                  onClick={() => cancelStuckIndexing(open.id, a.id)}
                                  className="inline-flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase border border-rose-400 text-rose-700 hover:bg-rose-500 hover:text-cream hover:border-rose-500 px-2 py-1 rounded-sm transition-colors"
                                  data-testid={`asset-cancel-stuck-${a.id}`}
                                  title="Sembra bloccato — annulla e riprova"
                                >
                                  <AlertCircle size={11} /> Annulla e riprova
                                </button>
                              )}
                              {a.content_type === "gdrive/folder" && (
                                <button
                                  onClick={() => refreshGdrive(a.id)}
                                  className="inline-flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase border border-gold/40 text-gold hover:bg-gold hover:text-cream px-2 py-1 rounded-sm transition-colors"
                                  data-testid={`asset-gdrive-refresh-${a.id}`}
                                  title="Re-indicizza cartella Drive"
                                >
                                  <RefreshCw size={11} /> Re-indicizza
                                </button>
                              )}
                              {(a.content_type === "gdrive/folder" || a.content_type === "external/url") && a.external_url && (
                                <a
                                  href={a.external_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase border border-ink/30 text-ink hover:bg-ink hover:text-cream px-2 py-1 rounded-sm transition-colors"
                                  data-testid={`asset-open-${a.id}`}
                                  title="Apri in nuova scheda"
                                >
                                  <ExternalLink size={11} /> Apri
                                </a>
                              )}
                              <button onClick={() => downloadAsset(a)} className="text-slate1 hover:text-ink" data-testid={`asset-dl-${a.id}`}><Download size={14} /></button>
                              {isPreviewable(a) && (
                                <button onClick={() => previewAsset(a)} disabled={previewLoading} className="text-slate1 hover:text-ink disabled:opacity-50" data-testid={`asset-preview-${a.id}`} aria-label="Anteprima"><Eye size={14} /></button>
                              )}
                              {a.kind === "listino" && !a.archived && a.content_type !== "external/url" && a.content_type !== "gdrive/folder" && (
                                <button
                                  onClick={() => setAssetArchived(open.id, a.id, true)}
                                  className="inline-flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase border border-slate-300 text-slate-600 hover:bg-slate-600 hover:text-cream hover:border-slate-600 px-2 py-1 rounded-sm transition-colors"
                                  data-testid={`asset-archive-${a.id}`}
                                  title="Sposta in Vecchi listini · nascosto agli utenti, fuori da ricerche e indice"
                                >
                                  <Archive size={11} /> Vecchi listini
                                </button>
                              )}
                              {a.archived && (
                                <button
                                  onClick={() => setAssetArchived(open.id, a.id, false)}
                                  className="inline-flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase border border-emerald-400 text-emerald-700 hover:bg-emerald-600 hover:text-cream hover:border-emerald-600 px-2 py-1 rounded-sm transition-colors"
                                  data-testid={`asset-restore-${a.id}`}
                                  title="Ripristina · torna visibile e ricercabile (re-indicizzato)"
                                >
                                  <ArchiveRestore size={11} /> Ripristina
                                </button>
                              )}
                              {!a.archived && (
                                <select
                                  value={a.kind}
                                  onChange={(e) => changeAssetKind(open.id, a.id, e.target.value)}
                                  className="border border-ink/15 bg-cream px-2 py-1 rounded-sm text-[11px] text-ink focus:outline-none focus:border-ink"
                                  title="Cambia tipo asset"
                                  data-testid={`asset-kind-${a.id}`}
                                >
                                  {ASSET_KINDS.map((k) => (
                                    <option key={k.value} value={k.value}>{k.label}</option>
                                  ))}
                                </select>
                              )}
                              {a.content_type !== "external/url" && a.content_type !== "gdrive/folder" && !a.archived && (
                                <label
                                  className="text-slate1 hover:text-gold cursor-pointer transition-colors"
                                  title="Sostituisci file binario (mantiene metadati e ri-indicizza)"
                                  data-testid={`asset-replace-label-${a.id}`}
                                >
                                  <RefreshCw size={14} />
                                  <input
                                    type="file"
                                    className="hidden"
                                    onChange={(e) => replaceAssetFile(open.id, a.id, e)}
                                    data-testid={`asset-replace-${a.id}`}
                                  />
                                </label>
                              )}
                              <button onClick={() => delAsset(open.id, a.id)} className="text-slate1 hover:text-destructive" data-testid={`asset-del-${a.id}`}><Trash2 size={14} /></button>
                              </div>
                            </div>
                            );
                          })}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>
              </div>
              {/* RIGHT COLUMN: Commercial editor + Studio discounts */}
              <div className="space-y-6">

              <CommercialEditor supplier={open} onSaved={async () => { await reload(); const r = await api.get(`/suppliers/${open.id}`); setOpen(r.data); }} />

              <div data-testid="sup-studio-discounts">
                <div className="flex items-center gap-2 mb-2">
                  <Tag size={12} className="text-gold" />
                  <div className="text-[10px] tracking-[0.2em] uppercase text-slate1">Sconti riservati · Studi & Trader</div>
                </div>
                <div className="bg-white border border-ink/10 rounded-sm">
                  <div className="p-3 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-start">
                    <div>
                      <StudioTraderCombobox
                        options={studios.filter((st) => !discounts.some((d) => d.user_id === st.id))}
                        value={selStudio}
                        onChange={setSelStudio}
                        testidPrefix="sup-discount-studio"
                      />
                    </div>
                    <button
                      onClick={addDiscount}
                      className="bg-ink text-cream px-4 py-2 text-[11px] tracking-[0.2em] uppercase rounded-sm"
                      data-testid="sup-discount-add"
                    >
                      <Plus size={12} className="inline mr-1" /> Aggiungi
                    </button>
                    <div className="md:col-span-2">
                      <label className="text-[10px] tracking-[0.2em] uppercase text-slate1 block mb-1">Sconto a catena</label>
                      <DiscountChainInput
                        value={selChain}
                        onChange={setSelChain}
                        testidPrefix="sup-discount-chain"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-[10px] tracking-[0.2em] uppercase text-slate1 block mb-1">Nota riservata per lo studio (visibile dallo studio)</label>
                      <textarea
                        value={selAdminNote}
                        onChange={(e) => setSelAdminNote(e.target.value)}
                        placeholder="Es. Sconto valido fino al 31/12/2026. Contattare l'agente per ordini > 50k€."
                        rows={2}
                        className="w-full border border-ink/20 px-3 py-2 rounded-sm bg-white text-sm resize-none"
                        data-testid="sup-discount-note"
                      />
                    </div>
                  </div>
                  <div className="border-t border-ink/10 divide-y divide-ink/10">
                    {discounts.length === 0 && (
                      <div className="p-3 text-xs text-slate1 italic">
                        Nessuno sconto personalizzato. Tutti gli studi vedono il {open.discount_pct || 0}% di default.
                      </div>
                    )}
                    {discounts.map((d) => {
                      const isEditing = editNote[d.user_id] !== undefined;
                      const currentNote = isEditing ? editNote[d.user_id] : (d.admin_note || "");
                      return (
                      <div key={d.user_id} className="p-3 space-y-2" data-testid={`sup-discount-${d.user_id}`}>
                        <div className="flex items-center gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-ink truncate">{d.studio || d.name || d.email}</div>
                            <div className="text-xs text-slate1 truncate">{d.email}</div>
                          </div>
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-gold/15 text-gold rounded-sm text-[11px] tracking-[0.15em] uppercase">
                            <Tag size={11} />
                            {d.discount_chain && d.discount_chain.length > 1
                              ? `${formatChain(d.discount_chain)} (${d.discount_pct}%)`
                              : `${d.discount_pct}%`}
                          </span>
                          <button
                            onClick={() => removeDiscount(d.user_id)}
                            className="text-slate1 hover:text-destructive"
                            data-testid={`sup-discount-del-${d.user_id}`}
                            aria-label="Rimuovi sconto"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                        <div className="flex items-start gap-2">
                          <textarea
                            value={currentNote}
                            onChange={(e) => setEditNote((m) => ({ ...m, [d.user_id]: e.target.value }))}
                            placeholder="Nota riservata per questo studio…"
                            rows={2}
                            className="flex-1 border border-ink/15 px-2.5 py-1.5 rounded-sm bg-cream2 text-xs resize-none"
                            data-testid={`sup-discount-note-edit-${d.user_id}`}
                          />
                          {isEditing && (
                            <button
                              onClick={() => updateDiscountNote(d)}
                              className="bg-ink text-cream px-3 py-1.5 text-[10px] tracking-[0.2em] uppercase rounded-sm whitespace-nowrap"
                              data-testid={`sup-discount-note-save-${d.user_id}`}
                            >
                              Salva
                            </button>
                          )}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {preview && (
        <div
          className="fixed inset-0 z-[100] bg-ink/85 backdrop-blur-sm flex flex-col"
          onClick={closePreview}
          data-testid="asset-preview-modal"
        >
          <div className="flex items-center justify-between px-6 py-3 bg-ink text-cream" onClick={(e) => e.stopPropagation()}>
            <div className="text-sm tracking-[0.15em] uppercase truncate">{preview.name}</div>
            <button
              onClick={closePreview}
              className="w-9 h-9 flex items-center justify-center hover:bg-cream/10 rounded-sm"
              data-testid="asset-preview-close"
              aria-label="Chiudi anteprima"
            >
              <X size={18} />
            </button>
          </div>
          <div className="flex-1 bg-cream2" onClick={(e) => e.stopPropagation()}>
            {preview.isPdf ? (
              <iframe src={preview.url} title={preview.name} className="w-full h-full border-0" data-testid="asset-preview-iframe" />
            ) : preview.isImage ? (
              <div className="w-full h-full flex items-center justify-center p-4">
                <img src={preview.url} alt={preview.name} className="max-w-full max-h-full object-contain" />
              </div>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-slate1 text-sm">Anteprima non supportata.</div>
            )}
          </div>
        </div>
      )}

      {requestsOpen && (
        <div className="fixed inset-0 z-[110] bg-ink/60 backdrop-blur-sm flex items-center justify-center p-4 md:p-8" onClick={() => setRequestsOpen(false)} data-testid="discount-requests-modal">
          <div className="bg-cream w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-sm" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 bg-ink text-cream flex items-center justify-between sticky top-0">
              <div className="flex items-center gap-2">
                <MailQuestion size={18} className="text-gold" />
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-cream/60">Richieste sconto</div>
                  <div className="font-display text-lg">{requests.length} richiest{requests.length === 1 ? "a" : "e"} in attesa</div>
                </div>
              </div>
              <button onClick={() => setRequestsOpen(false)} className="w-9 h-9 flex items-center justify-center hover:bg-cream/10 rounded-sm" aria-label="Chiudi" data-testid="discount-requests-close">
                <X size={18} />
              </button>
            </div>

            <div className="p-6 space-y-3">
              {requests.length === 0 && (
                <div className="text-center text-slate1 py-8 text-sm">
                  Nessuna richiesta in sospeso al momento.
                </div>
              )}
              {requests.map((req) => {
                const ctx = reqContext[req.id];
                return (
                <div key={req.id} className="bg-white border border-ink/10 rounded-sm p-4" data-testid={`discount-req-${req.id}`}>
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] tracking-[0.2em] uppercase text-slate1">Studio</div>
                      <div className="font-display text-base text-ink truncate">{req.studio || req.user_name || req.user_email}</div>
                      <div className="text-xs text-slate1 truncate">{req.user_email}</div>
                    </div>
                    <div className="text-[10px] tracking-[0.15em] uppercase text-slate1 text-right">
                      {new Date(req.created_at).toLocaleDateString("it-IT")}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mb-3 text-sm">
                    <Folder size={14} className="text-slate1" />
                    <span className="text-slate1">Listino</span>
                    <span className="text-ink font-medium">{req.supplier_name}</span>
                  </div>

                  {/* Decision context box: condizioni admin + chain peer */}
                  {ctx && (
                    <div className="bg-cream2 border border-gold/30 rounded-sm p-3 mb-3 space-y-2.5" data-testid={`discount-req-context-${req.id}`}>
                      {/* Admin's own conditions */}
                      <div>
                        <div className="text-[9px] tracking-[0.2em] uppercase text-gold">Le tue condizioni d&apos;acquisto</div>
                        <div className="text-sm text-ink mt-0.5">
                          {ctx.admin_conditions?.chain?.length && ctx.admin_conditions.chain.some(v => v > 0)
                            ? <><span className="font-medium tabular-nums">{ctx.admin_conditions.chain.filter(v => v > 0).join("+")}%</span><span className="text-slate1 ml-1.5 text-xs">= {cascadePct(ctx.admin_conditions.chain)}% effettivo</span></>
                            : (ctx.admin_conditions?.discount_pct > 0
                              ? <span className="font-medium tabular-nums">{ctx.admin_conditions.discount_pct}%</span>
                              : <span className="text-slate1 italic">non configurate · imposta nella scheda fornitore</span>)
                          }
                          {ctx.admin_conditions?.payment_terms && (
                            <span className="text-slate1 ml-2 text-xs">· {ctx.admin_conditions.payment_terms}</span>
                          )}
                        </div>
                      </div>

                      {/* Peer assignments */}
                      {Array.isArray(ctx.peers) && ctx.peers.length > 0 ? (
                        <div>
                          <div className="text-[9px] tracking-[0.2em] uppercase text-slate1">
                            Praticato a {ctx.peer_stats?.count || ctx.peers.length} altr{ctx.peer_stats?.count === 1 ? "o" : "i"}
                            {ctx.peer_stats?.count > 1 && (
                              <span className="ml-1.5 text-slate1">· mediana <span className="text-ink font-medium tabular-nums">{ctx.peer_stats.median}%</span> · min {ctx.peer_stats.min}% · max {ctx.peer_stats.max}%</span>
                            )}
                          </div>
                          <div className="mt-1.5 flex flex-wrap gap-1.5">
                            {ctx.peers.slice(0, 6).map((p, i) => (
                              <button
                                key={i}
                                type="button"
                                onClick={() => {
                                  // Click → applica direttamente la chain di questo peer
                                  const ch = (p.chain && p.chain.length) ? p.chain : [p.discount_pct || 0, 0, 0];
                                  setReqAnswerChain((m) => ({ ...m, [req.id]: ch }));
                                }}
                                className="px-2 py-1 bg-white hover:bg-ink hover:text-cream border border-ink/15 rounded-sm text-[11px] inline-flex items-center gap-1.5 transition-colors"
                                data-testid={`discount-req-peer-${req.id}-${i}`}
                                title={`Applica catena di ${p.studio}`}
                              >
                                <span className="truncate max-w-[140px]">{p.studio}</span>
                                <span className="font-medium tabular-nums">{(p.chain && p.chain.filter(v=>v>0).length) ? p.chain.filter(v=>v>0).join("+") : p.discount_pct}%</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="text-[11px] text-slate1 italic">Nessuno sconto ancora praticato ad altri su questo fornitore.</div>
                      )}

                      {/* Suggested chain → quick-fill */}
                      {Array.isArray(ctx.suggested_chain) && ctx.suggested_chain.some(v => v > 0) && (
                        <div className="pt-1 border-t border-gold/20 flex items-center justify-between gap-2">
                          <div className="text-[10px] tracking-[0.15em] uppercase text-slate1">
                            Suggerito {ctx.suggested_source === "peer_median" ? "(in linea con altri)" : ctx.suggested_source === "peer_median_pct" ? "(mediana altri)" : "(tue condizioni −5%)"}:
                            <span className="ml-1.5 text-ink font-medium tabular-nums">{ctx.suggested_chain.filter(v => v > 0).join("+")}%</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => setReqAnswerChain((m) => ({ ...m, [req.id]: ctx.suggested_chain }))}
                            className="px-3 py-1 bg-gold text-ink text-[10px] tracking-[0.15em] uppercase rounded-sm hover:bg-ink hover:text-cream transition-colors"
                            data-testid={`discount-req-apply-suggested-${req.id}`}
                          >
                            Usa suggerito
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="space-y-3">
                    <div>
                      <label className="text-[10px] tracking-[0.2em] uppercase text-slate1 block mb-1">Sconto a catena da assegnare</label>
                      <DiscountChainInput
                        value={reqAnswerChain[req.id] || []}
                        onChange={(arr) => setReqAnswerChain({ ...reqAnswerChain, [req.id]: arr })}
                        testidPrefix={`discount-req-chain-${req.id}`}
                      />
                    </div>
                    <div>
                      <label className="text-[10px] tracking-[0.2em] uppercase text-slate1 block mb-1">Nota per lo studio (visibile dopo l&apos;approvazione)</label>
                      <textarea
                        value={reqAnswerNote[req.id] || ""}
                        onChange={(e) => setReqAnswerNote({ ...reqAnswerNote, [req.id]: e.target.value })}
                        placeholder="Es. Sconto valido fino al 31/12/2026. Per ordini > 50k€ contattare direttamente l'agente."
                        rows={2}
                        className="w-full border border-ink/20 px-3 py-2 rounded-sm bg-white text-sm resize-none"
                        data-testid={`discount-req-note-${req.id}`}
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => answerRequest(req)}
                        className="bg-ink text-cream px-4 py-2.5 text-[11px] tracking-[0.2em] uppercase rounded-sm flex-1"
                        data-testid={`discount-req-approve-${req.id}`}
                      >
                        Approva accesso & sconto
                      </button>
                      <button
                        onClick={() => rejectRequest(req)}
                        className="border border-ink/20 text-slate1 px-4 py-2.5 text-[11px] tracking-[0.2em] uppercase rounded-sm hover:bg-destructive/10 hover:text-destructive"
                        data-testid={`discount-req-reject-${req.id}`}
                      >
                        Rifiuta
                      </button>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      <AgentDirectoryModal
        open={agentDirectoryOpen}
        onClose={() => setAgentDirectoryOpen(false)}
        onJumpSupplier={jumpToSupplier}
      />
    </div>
  );
}

function CommercialEditor({ supplier, onSaved }) {
  const initial = React.useMemo(() => ({
    name: supplier.name || "",
    description: supplier.description || "",
    lead_time_days: supplier.lead_time_days || 0,
    discount_pct: supplier.discount_pct || 0,
    discount_chain: (supplier.discount_chain && supplier.discount_chain.length)
      ? supplier.discount_chain
      : (supplier.discount_pct ? [supplier.discount_pct, 0, 0] : []),
    website: supplier.website || "",
    purchase_discount_pct: supplier.purchase_discount_pct || 0,
    purchase_discount_chain: (supplier.purchase_discount_chain && supplier.purchase_discount_chain.length)
      ? supplier.purchase_discount_chain
      : (supplier.purchase_discount_pct ? [supplier.purchase_discount_pct, 0, 0] : []),
    payment_terms: supplier.payment_terms || "",
    agent_name: supplier.agent_name || "",
    agent_phone: supplier.agent_phone || "",
    agent_email: supplier.agent_email || "",
    company_contact_name: supplier.company_contact_name || "",
    contact_phone: supplier.contact_phone || "",
    orders_email: supplier.orders_email || "",
    year_end_bonus_budget: supplier.year_end_bonus_budget || 0,
    price_increase_pct: supplier.price_increase_pct || 0,
    price_increase_date: supplier.price_increase_date || "",
    price_increase_new_listino: Boolean(supplier.price_increase_new_listino),
    order_deadline_date: supplier.order_deadline_date || "",
    reserved_area_url: supplier.reserved_area_url || "",
    reserved_area_username: supplier.reserved_area_username || "",
    reserved_area_password: "",  // input-only: vuoto = non toccare salvata; valore = sovrascrivi
    notes: supplier.notes || "",
  }), [supplier]);
  const [form, setForm] = React.useState(initial);
  const [saving, setSaving] = React.useState(false);
  const [openingArea, setOpeningArea] = React.useState(false);
  // Visibilità password area riservata: false = type=password (••••), true = type=text leggibile.
  // Quando l'admin abilita la visibilità per la PRIMA volta su un fornitore che ha già
  // una password salvata e il campo è vuoto, fa una fetch a /admin/suppliers/{id}/credentials
  // per recuperarla in chiaro così l'admin può leggerla/correggerla.
  const [showAreaPwd, setShowAreaPwd] = React.useState(false);
  const [loadingPwd, setLoadingPwd] = React.useState(false);
  async function toggleAreaPwdVisibility() {
    if (showAreaPwd) {
      setShowAreaPwd(false);
      return;
    }
    // Se ho già un valore nel form (utente l'ha tipizzato), basta togglare visibilità
    if (form.reserved_area_password) {
      setShowAreaPwd(true);
      return;
    }
    // Altrimenti fetch della password salvata dal backend (audit-loggata server-side)
    if (!supplier?.id || !supplier.reserved_area_username) {
      setShowAreaPwd(true);
      return;
    }
    setLoadingPwd(true);
    try {
      const { data } = await api.get(`/admin/suppliers/${supplier.id}/credentials`);
      if (data?.has_password && data.password) {
        setForm((f) => ({ ...f, reserved_area_password: data.password }));
      }
      setShowAreaPwd(true);
    } catch {
      toast.error("Impossibile recuperare la password");
    } finally {
      setLoadingPwd(false);
    }
  }
  // Mini-rubrica agenti: lista di {name, email, phone, supplier_names[]} caricata 1 volta.
  const [agentDirectory, setAgentDirectory] = React.useState([]);
  const [agentSuggestOpen, setAgentSuggestOpen] = React.useState(false);
  React.useEffect(() => {
    api.get("/admin/supplier-agents").then((r) => setAgentDirectory(r.data || [])).catch(() => {});
  }, []);
  const agentMatches = React.useMemo(() => {
    const q = (form.agent_name || "").trim().toLowerCase();
    if (!q) return [];
    return agentDirectory
      .filter((a) => a.name.toLowerCase().includes(q) || a.email.toLowerCase().includes(q))
      .filter((a) => a.name.toLowerCase() !== q || a.email.toLowerCase() !== (form.agent_email || "").toLowerCase()) // non riproporre già selezionato esatto
      .slice(0, 6);
  }, [form.agent_name, form.agent_email, agentDirectory]);

  function applyAgent(a) {
    setForm({ ...form, agent_name: a.name, agent_email: a.email, agent_phone: a.phone });
    setAgentSuggestOpen(false);
  }

  // Conflitto: l'email corrisponde a un agente in rubrica con nome diverso.
  // Esclude il fornitore corrente dalla rilevazione.
  const emailConflictAgent = React.useMemo(() => {
    const email = (form.agent_email || "").trim().toLowerCase();
    const name = (form.agent_name || "").trim().toLowerCase();
    if (!email) return null;
    return agentDirectory.find((a) => {
      const aEmail = (a.email || "").toLowerCase();
      const aName = (a.name || "").toLowerCase();
      // Esclude il record stesso (matching: nome+email originali del supplier in editing).
      const isSelf = aEmail === (supplier.agent_email || "").toLowerCase() && aName === (supplier.agent_name || "").toLowerCase();
      if (isSelf) return false;
      return aEmail === email && aName !== name;
    }) || null;
  }, [form.agent_email, form.agent_name, agentDirectory, supplier.agent_email, supplier.agent_name]);
  // Per "dirty" ignoriamo `reserved_area_password` (input-only, sempre stringa vuota all'apertura).
  const dirty = React.useMemo(() => {
    const a = { ...form }; delete a.reserved_area_password;
    const b = { ...initial }; delete b.reserved_area_password;
    return JSON.stringify(a) !== JSON.stringify(b) || (form.reserved_area_password || "") !== "";
  }, [form, initial]);

  async function save() {
    setSaving(true);
    try {
      const payload = {
        ...form,
        lead_time_days: Number(form.lead_time_days || 0),
        discount_pct: Number(form.discount_pct || 0),
        purchase_discount_pct: Number(form.purchase_discount_pct || 0),
        year_end_bonus_budget: Number(form.year_end_bonus_budget || 0),
        price_increase_pct: Number(form.price_increase_pct || 0),
        price_increase_date: form.price_increase_date || "",
        price_increase_new_listino: Boolean(form.price_increase_new_listino),
        order_deadline_date: form.order_deadline_date || "",
      };
      // Se la password resta vuota all'invio significa "non toccare" → ometto la chiave
      // così il backend non sovrascrive l'enc esistente. Se l'utente vuole rimuovere,
      // userà il bottone "Rimuovi password" (vedi sotto) che imposta uno spazio gestito a parte.
      if (payload.reserved_area_password === "") {
        delete payload.reserved_area_password;
      }
      await api.put(`/admin/suppliers/${supplier.id}`, payload);
      toast.success("Salvato");
      setForm((f) => ({ ...f, reserved_area_password: "" }));
      if (onSaved) await onSaved();
    } catch { toast.error("Errore salvataggio"); }
    finally { setSaving(false); }
  }

  // --- Apri area riservata con auto-copy username + popup password ---
  async function openReservedArea() {
    if (!supplier.reserved_area_url) {
      toast.error("Nessun URL configurato");
      return;
    }
    setOpeningArea(true);
    // Pre-apri tab subito (richiede gesto utente, altrimenti popup blocker)
    const newTab = window.open(supplier.reserved_area_url, "_blank", "noopener,noreferrer");
    try {
      const { data } = await api.get(`/admin/suppliers/${supplier.id}/credentials`);
      if (!data.username && !data.password) {
        toast.warning("Nessuna credenziale salvata per questo fornitore");
        return;
      }
      // Copia username negli appunti
      try {
        await navigator.clipboard.writeText(data.username || "");
        toast.success(`Username "${data.username}" copiato negli appunti · premi Ctrl+V nel campo del sito`, { duration: 6000 });
      } catch {
        toast.error("Impossibile accedere agli appunti. Username: " + data.username);
      }
      // Apri popup persistente con password (gestito dal parent → useState)
      window.__archihubSupplierCreds = data;
      window.dispatchEvent(new CustomEvent("archihub:show-creds", { detail: { supplierName: supplier.name, ...data } }));
      if (newTab) newTab.focus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore lettura credenziali");
    } finally {
      setOpeningArea(false);
    }
  }

  const lbl = "block text-[10px] tracking-[0.2em] uppercase text-slate1 mb-1";
  const inp = "w-full border border-ink/20 px-3 py-2 rounded-sm bg-white text-sm focus:outline-none focus:border-ink";

  return (
    <div className="bg-white border border-ink/10 p-5 rounded-sm" data-testid="sup-commercial">
      <div className="flex items-center justify-between mb-4">
        <div className="text-[10px] tracking-[0.2em] uppercase text-gold inline-flex items-center gap-1.5">
          <Lock size={10} /> Solo admin · informazioni commerciali
        </div>
        {dirty && (
          <button
            onClick={save}
            disabled={saving}
            className="bg-ink text-cream px-4 py-1.5 text-[10px] tracking-[0.2em] uppercase rounded-sm disabled:opacity-50"
            data-testid="sup-commercial-save"
          >
            {saving ? "Salvataggio..." : "Salva modifiche"}
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className={lbl}>Lead time (gg)</label>
          <input type="number" min={0} value={form.lead_time_days} onChange={(e) => setForm({ ...form, lead_time_days: e.target.value })} className={inp} data-testid="sup-com-lead" />
        </div>
        <div>
          <label className={lbl}>Sconto riservato studio</label>
          <DiscountChainInput
            value={form.discount_chain}
            onChange={(arr) => setForm({ ...form, discount_chain: arr })}
            testidPrefix="sup-com-discount-chain"
          />
        </div>
        <div>
          <label className={lbl}>Sconto acquisto</label>
          <DiscountChainInput
            value={form.purchase_discount_chain}
            onChange={(arr) => setForm({ ...form, purchase_discount_chain: arr })}
            testidPrefix="sup-com-purchase-chain"
          />
        </div>
        <div>
          <label className={lbl}>Pagamento</label>
          <input value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} placeholder="es. 30/60 gg fm" className={inp} data-testid="sup-com-payment" />
        </div>
        <div className="relative">
          <label className={lbl}>
            Agente
            {agentDirectory.length > 0 && (
              <span className="ml-2 text-[9px] text-slate1 normal-case tracking-normal">
                · digita per riutilizzare ({agentDirectory.length} in rubrica)
              </span>
            )}
          </label>
          <input
            value={form.agent_name}
            onChange={(e) => { setForm({ ...form, agent_name: e.target.value }); setAgentSuggestOpen(true); }}
            onFocus={() => setAgentSuggestOpen(true)}
            onBlur={() => setTimeout(() => setAgentSuggestOpen(false), 150)}
            className={inp}
            autoComplete="off"
            data-testid="sup-com-agent"
          />
          {agentSuggestOpen && agentMatches.length > 0 && (
            <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-ink/20 rounded-sm shadow-lg max-h-60 overflow-auto" data-testid="sup-com-agent-suggestions">
              {agentMatches.map((a, idx) => (
                <button
                  type="button"
                  key={`${a.name}-${a.email}-${idx}`}
                  onMouseDown={(e) => { e.preventDefault(); applyAgent(a); }}
                  className="w-full text-left px-3 py-2 hover:bg-cream2 border-b border-ink/10 last:border-b-0"
                  data-testid={`sup-com-agent-sugg-${idx}`}
                >
                  <div className="text-sm text-ink font-medium">{a.name}</div>
                  <div className="text-[10px] text-slate1 flex flex-wrap gap-x-3 mt-0.5">
                    {a.email && <span>📧 {a.email}</span>}
                    {a.phone && <span>📞 {a.phone}</span>}
                  </div>
                  {a.supplier_names?.length > 0 && (
                    <div className="text-[9px] tracking-[0.15em] uppercase text-slate1 mt-0.5">
                      Già su: {a.supplier_names.slice(0, 3).join(", ")}{a.supplier_names.length > 3 ? "…" : ""}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
        <div>
          <label className={lbl}>Telefono agente</label>
          <input value={form.agent_phone} onChange={(e) => setForm({ ...form, agent_phone: e.target.value })} className={inp} data-testid="sup-com-agent-phone" />
        </div>
        <div>
          <label className={lbl}>Email agente</label>
          <input type="email" value={form.agent_email} onChange={(e) => setForm({ ...form, agent_email: e.target.value })} placeholder="agente@dominio.it" className={`${inp} ${emailConflictAgent ? "border-rose-500 ring-2 ring-rose-200" : ""}`} data-testid="sup-com-agent-email" />
          {emailConflictAgent && (
            <div className="mt-1.5 text-[10px] text-rose-700 leading-snug" data-testid="sup-com-agent-email-conflict">
              ⚠ Questa email è già di <strong>{emailConflictAgent.name}</strong>
              {emailConflictAgent.supplier_names?.length > 0 && (
                <span> (su {emailConflictAgent.supplier_names.slice(0, 2).join(", ")}{emailConflictAgent.supplier_names.length > 2 ? "…" : ""})</span>
              )}.
              <button
                type="button"
                onClick={() => setForm({ ...form, agent_name: emailConflictAgent.name, agent_phone: emailConflictAgent.phone, agent_email: emailConflictAgent.email })}
                className="ml-2 underline hover:text-rose-900"
              >
                Usa quello esistente
              </button>
            </div>
          )}
        </div>
        <div>
          <label className={lbl}>Referente azienda</label>
          <input value={form.company_contact_name} onChange={(e) => setForm({ ...form, company_contact_name: e.target.value })} placeholder="Nome e cognome" className={inp} data-testid="sup-com-contact-name" />
        </div>
        <div>
          <label className={lbl}>Telefono azienda</label>
          <input type="tel" value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} placeholder="+39 …" className={inp} data-testid="sup-com-contact-phone" />
        </div>
        <div>
          <label className={lbl}>Email ordini al fornitore</label>
          <input type="email" value={form.orders_email} onChange={(e) => setForm({ ...form, orders_email: e.target.value })} placeholder="ordini@fornitore.it" className={inp} data-testid="sup-com-orders-email" />
        </div>
        <div className="md:col-span-2">
          <label className={lbl}>Budget premio fine anno (€)</label>
          <input type="number" min={0} step="100" value={form.year_end_bonus_budget} onChange={(e) => setForm({ ...form, year_end_bonus_budget: e.target.value })} className={inp} data-testid="sup-com-budget" />
        </div>
        <div>
          <label className={lbl}>Aumento listino (%)</label>
          <input type="number" min={0} step="0.5" value={form.price_increase_pct} onChange={(e) => setForm({ ...form, price_increase_pct: e.target.value })} placeholder="es. 5" disabled={Boolean(form.price_increase_new_listino)} className={`${inp} ${form.price_increase_new_listino ? "opacity-50 cursor-not-allowed" : ""}`} data-testid="sup-com-increase-pct" />
          <label className="mt-1.5 flex items-center gap-1.5 text-[11px] text-slate1 cursor-pointer select-none">
            <input type="checkbox" checked={Boolean(form.price_increase_new_listino)} onChange={(e) => setForm({ ...form, price_increase_new_listino: e.target.checked, ...(e.target.checked ? { price_increase_pct: 0 } : {}) })} data-testid="sup-com-increase-newlistino" />
            Nuovo listino (senza %)
          </label>
        </div>
        <div>
          <label className={lbl}>Decorrenza aumento</label>
          <input type="date" value={form.price_increase_date} onChange={(e) => setForm({ ...form, price_increase_date: e.target.value })} className={inp} data-testid="sup-com-increase-date" />
        </div>
        <div className="md:col-span-2">
          <label className={lbl}>Termine ordine per consegne</label>
          <input type="date" value={form.order_deadline_date} onChange={(e) => setForm({ ...form, order_deadline_date: e.target.value })} className={inp} data-testid="sup-com-order-deadline" />
          <div className="mt-1 text-[10px] text-slate1 italic">Data limite per ordinare e garantire la consegna nel prossimo lotto. Visibile a Studio e Trader, evidenziato quando ≤ 15 giorni.</div>
        </div>
        <div className="md:col-span-2">
          <label className={lbl}>Sito web</label>
          <input type="url" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://www.fornitore.com" className={inp} data-testid="sup-com-website" />
        </div>

        {/* Area riservata fornitore: credenziali B2B */}
        <div className="md:col-span-2 border-t border-ink/10 pt-4 mt-2">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] tracking-[0.2em] uppercase text-ink inline-flex items-center gap-1.5">
              <Lock size={11} /> Area riservata fornitore
            </div>
            {supplier.reserved_area_url && (
              <button
                type="button"
                onClick={openReservedArea}
                disabled={openingArea}
                className="bg-ink text-cream px-4 py-2 text-[10px] tracking-[0.2em] uppercase rounded-sm hover-lift disabled:opacity-50 inline-flex items-center gap-2"
                data-testid="sup-open-reserved-area"
                title="Apre il sito fornitore in nuova tab, copia username negli appunti e mostra popup con password"
              >
                <Link2 size={11} />
                {openingArea ? "Apertura…" : "Apri area riservata"}
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <label className={lbl}>URL login area riservata</label>
              <input type="url" value={form.reserved_area_url} onChange={(e) => setForm({ ...form, reserved_area_url: e.target.value })} placeholder="https://b2b.fornitore.it/login" className={inp} data-testid="sup-com-area-url" />
            </div>
            <div>
              <label className={lbl}>Username / Email</label>
              <input type="text" autoComplete="off" value={form.reserved_area_username} onChange={(e) => setForm({ ...form, reserved_area_username: e.target.value })} placeholder="italianmood@email.it" className={inp} data-testid="sup-com-area-username" />
            </div>
            <div>
              <label className={lbl}>Password {supplier.reserved_area_username ? "(click 👁 per leggere quella salvata)" : ""}</label>
              <div className="relative">
                <input
                  type={showAreaPwd ? "text" : "password"}
                  autoComplete="new-password"
                  value={form.reserved_area_password}
                  onChange={(e) => setForm({ ...form, reserved_area_password: e.target.value })}
                  placeholder="••••••••"
                  className={`${inp} pr-9 font-mono`}
                  data-testid="sup-com-area-password"
                />
                <button
                  type="button"
                  onClick={toggleAreaPwdVisibility}
                  disabled={loadingPwd}
                  className="absolute right-1 top-1/2 -translate-y-1/2 p-1.5 text-slate1 hover:text-ink disabled:opacity-40"
                  title={showAreaPwd ? "Nascondi password" : "Mostra password salvata"}
                  data-testid="sup-com-area-password-toggle"
                  aria-label={showAreaPwd ? "Nascondi password" : "Mostra password"}
                >
                  {loadingPwd ? <Loader2 size={14} className="animate-spin" /> : (showAreaPwd ? <EyeOff size={14} /> : <Eye size={14} />)}
                </button>
              </div>
            </div>
          </div>
          <div className="mt-2 text-[10px] text-slate1 italic">
            La password viene cifrata sul server. Click &ldquo;Apri area riservata&rdquo; → si apre il sito + username negli appunti + popup con bottone copia password.
          </div>
        </div>

        <div className="md:col-span-2">
          <label className={lbl}>Note</label>
          <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} className={`${inp} resize-y`} data-testid="sup-com-notes" />
        </div>
      </div>
    </div>
  );
}
